import React, { useEffect, useState } from "react";
import { goToCheckout } from "@/lib/checkout";
import LostResponsesReview from "@/components/LostResponsesReview";
import { LiveBoostViews } from "@/components/LiveBoostViews";
import { FormCard } from "@/components/FormShell";
import { FindoSpinner, WaitingNotice, PageLoader } from "@/components/FindoSpinner";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, uploadFile } from "@/lib/api";
import { API, getB2CToken } from "@/lib/api";
import { useB2C } from "@/context/B2CAuthContext";
import { useLang } from "@/context/LanguageContext";
import { formatMoney } from "@/lib/money";
import Pagination, { paginate } from "@/components/Pagination";
import { RecaptchaNotice } from "@/components/RecaptchaNotice";
import { IMAGES } from "@/lib/images";
import { COUNTRIES } from "@/lib/countries";
import { labelFor, statusCls, CATEGORIES } from "@/lib/categories";
import { formatRemaining, useTicker, isExpiringSoon, isRecentlyExpired } from "@/lib/boost";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import BoostAdDialog from "@/components/BoostAdDialog";
import AvatarCropDialog from "@/components/AvatarCropDialog";
import { SocialLinks } from "@/components/SocialLinks";
import SupportSessionBanner from "@/components/SupportSessionBanner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { LogIn, UserPlus, Smartphone, KeyRound, LogOut, PackageSearch, PackageCheck, Handshake, ArrowRight, UserRound, Sparkles, Pencil, X, XCircle, Gift, Eye, Mail, BarChart3, ShieldCheck, Download, Trash2, Lock, Phone, User, Trophy, Camera, CheckCircle2, Award, Coins, Ticket, Bell, CalendarClock, RefreshCw, QrCode, Truck, Store, MapPin, ChevronDown, CreditCard, ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";
import { CancelReasonDialog } from "@/components/CancelReasonDialog";
import { ShippingPayModal } from "@/components/ShippingPayModal";
import { FinderReportsReview } from "@/components/FinderReportsReview";
import { VerificationAnswersForm } from "@/components/Verification";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { fileToCompressedDataUrl } from "@/lib/image";
import { AvatarImg } from "@/components/AvatarImg";

const AFRICA_CODES = new Set(["DZ","AO","BJ","BW","BF","BI","CM","CV","CF","TD","KM","CG","CD","CI","DJ","EG","GQ","ER","SZ","ET","GA","GM","GH","GN","GW","KE","LS","LR","LY","MG","MW","ML","MR","MU","MA","MZ","NA","NE","NG","RW","ST","SN","SC","SL","SO","ZA","SS","SD","TZ","TG","TN","UG","ZM","ZW"]);
const unlockFeeLabel = (country, lang) => formatMoney(AFRICA_CODES.has((country || "").toUpperCase()) ? 2.99 : 9.99, lang);
// Launch offer (temporary, pending Flutterwave): unlock is FREE for users whose
// PROFILE country is African. Distinction is by the user's profile, not the ad.
const isAfricaUser = (user) => AFRICA_CODES.has((user?.country || "").toUpperCase());
const FREE_UNLOCK_LABEL = "Frais de lancement offerts";

// Fullscreen photo viewer. Plain fixed overlay (not a nested Radix Dialog) so it
// layers cleanly above an open modal and closing it never touches the modal state.
// Supports click-to-zoom and left/right navigation for objects with several photos.
function PhotoLightbox({ src, images, index = 0, open, onClose }) {
  const { t } = useLang();
  const list = ((images && images.length ? images : (src ? [src] : [])) || []).filter(Boolean);
  const [i, setI] = useState(index);
  const [zoom, setZoom] = useState(false);
  useEffect(() => { if (open) { setI(index); setZoom(false); } }, [open, index]);
  if (!open || list.length === 0) return null;
  const many = list.length > 1;
  const go = (delta) => (e) => { e.stopPropagation(); setZoom(false); setI((p) => (p + delta + list.length) % list.length); };
  return (
    <div onClick={onClose}
      className="fixed inset-0 z-[9999] bg-black/85 flex items-center justify-center p-4 cursor-zoom-out"
      data-testid="photo-lightbox">
      <button onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-4 right-4 h-11 w-11 grid place-items-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors z-10"
        aria-label={t("hc.close")} data-testid="photo-lightbox-close">
        <X size={24} />
      </button>
      {many && (
        <button onClick={go(-1)}
          className="absolute left-3 sm:left-6 h-12 w-12 grid place-items-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors z-10"
          aria-label={t("hc.previousPhoto")} data-testid="photo-lightbox-prev">
          <ChevronLeft size={28} />
        </button>
      )}
      {many && (
        <button onClick={go(1)}
          className="absolute right-3 sm:right-6 h-12 w-12 grid place-items-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors z-10"
          aria-label={t("hc.nextPhoto")} data-testid="photo-lightbox-next">
          <ChevronRight size={28} />
        </button>
      )}
      <img src={list[i]} alt=""
        onClick={(e) => { e.stopPropagation(); setZoom((z) => !z); }}
        className={`max-h-[90vh] max-w-[92vw] rounded-2xl object-contain shadow-2xl transition-transform duration-200 ${zoom ? "scale-[1.8] cursor-zoom-out" : "scale-100 cursor-zoom-in"}`}
        data-testid="photo-lightbox-img" />
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-3 text-white/80 text-xs">
        {!zoom && <span className="inline-flex items-center gap-1"><ZoomIn size={13} /> {t("hc.clickToZoom")}</span>}
        {many && <span data-testid="photo-lightbox-counter" className="font-semibold">{i + 1} / {list.length}</span>}
      </div>
    </div>
  );
}


export default function MonCompte() {
  const { user, loading, login, register, logout } = useB2C();
  const { t } = useLang();

  if (loading) return <PageLoader label={t("c.loading")} className="max-w-md mx-auto py-32" />;

  return (
    <div className="bg-background">
      {user && <SupportSessionBanner role="b2c" />}
      <div className="relative h-56 overflow-hidden photo-scrim">
        <img src={IMAGES.reception} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-white px-6">
          <span className="inline-flex items-center gap-2 rounded-full bg-findo-emerald text-white px-3.5 py-1.5 text-xs font-bold uppercase tracking-wide">{t("mc.title")}</span>
          <h1 className="mt-4 font-display text-4xl md:text-5xl font-semibold">{t("mc.title")}</h1>
          <p className="mt-2 text-white/85 max-w-lg">{t("mc.subtitle")}</p>
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-6 py-12">
        {user ? <Dashboard user={user} logout={logout} t={t} /> : <AuthForms login={login} register={register} t={t} />}
      </div>
      {user && (
        <div className="max-w-3xl mx-auto px-6 pb-12 -mt-4 flex flex-col items-center gap-2" data-testid="b2c-social">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{t("mc2.followFindo")}</p>
          <SocialLinks size={16} />
        </div>
      )}
    </div>
  );
}

export function AuthForms({ login, register, t }) {
  const { forgot, resetPassword } = useB2C();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const resetToken = params.get("reset_token");
  const nextPath = params.get("next");
  const [mode, setMode] = useState(resetToken ? "reset" : "login");
  const [busy, setBusy] = useState(false);
  const [remember, setRemember] = useState(false);
  const [cguReg, setCguReg] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [recoverPhone, setRecoverPhone] = useState("");
  const [recoverMsg, setRecoverMsg] = useState("");
  const [form, setForm] = useState({
    email: (() => { try { return localStorage.getItem("findo_b2c_last_email") || ""; } catch { return ""; } })(),
    password: "", first_name: "", last_name: "", phone: "", country: "FR",
    pseudo: "", city: "", avatar_base64: null,
    leaderboard_opt_in: false, show_photo: false, show_name: true, show_city: false, show_country: false,
  });

  async function onAvatar(e) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      const compressed = await fileToCompressedDataUrl(f, { maxDim: 512, quality: 0.8 });
      setForm((s) => ({ ...s, avatar_base64: compressed }));
    } catch { toast.error(t("mc2.imgInvalid")); }
  }

  async function submit(e) {
    e.preventDefault();
    if (mode === "register" && form.password !== (form.confirmReg || "")) {
      toast.error(t("mc.passwordMismatch"));
      return;
    }
    if (mode === "register" && !cguReg) {
      toast.error(t("sub.cguRequired"));
      return;
    }
    setBusy(true);
    try {
      const mail = form.email.trim().toLowerCase();
      if (mode === "login") await login(mail, form.password, remember);
      else await register({ ...form, first_name: form.first_name.trim(), last_name: form.last_name.trim(), email: mail, cgu_accepted: true }, remember);
      try { localStorage.setItem("findo_b2c_last_email", mail); } catch { /* ignore */ }
      toast.success(t("mc.welcome"));
      // Return the user to the page they were trying to reach (e.g. a lost-item
      // declaration form), rather than leaving them on the account dashboard.
      if (nextPath && nextPath.startsWith("/")) navigate(nextPath, { replace: true });
    } catch (err) {
      const msg = err.message || "";
      if (mode === "login" && /mot de passe incorrect/i.test(msg)) {
        toast.error(msg, {
          action: { label: t("hc.reset"), onClick: () => setMode("forgot") },
          duration: 9000,
        });
      } else {
        toast.error(msg);
      }
    } finally { setBusy(false); }
  }

  async function submitForgot(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await forgot(form.email.trim().toLowerCase());
      setForgotSent(true);
      toast.success(t("mc.forgotSent"));
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  }

  async function recoverBySms(e) {
    e.preventDefault();
    if (!recoverPhone.trim()) return;
    setBusy(true);
    try {
      const res = await api(`/claim/recover-by-phone`, { method: "POST", auth: false, body: { phone: recoverPhone.trim() } });
      setRecoverMsg(res?.message || t("mc2.recoverSmsFallback"));
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  }

  async function submitReset(e) {
    e.preventDefault();
    if (form.password !== (form.confirm || "")) {
      toast.error(t("mc.passwordMismatch"));
      return;
    }
    setBusy(true);
    try {
      await resetPassword(resetToken, form.password);
      toast.success(t("mc.resetDone"));
      setParams({});
      setMode("login");
      setForm({ ...form, password: "", confirm: "" });
    } catch (err) {
      const msg = err.message || "";
      if (/invalide|expir|invalid|expired/i.test(msg)) {
        toast.error(t("mc.resetExpired"));
        setParams({});
        setForm({ ...form, password: "", confirm: "" });
        setMode("forgot");
      } else { toast.error(msg); }
    } finally { setBusy(false); }
  }

  if (mode === "reset" && resetToken) {
    return (
      <FormCard icon={KeyRound} title={t("mc.resetTitle")} className="max-w-md mx-auto" testid="b2c-reset-card">
        <form onSubmit={submitReset} className="space-y-6">
          <div><Label>{t("mc.newPassword")}</Label><PasswordInput required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="b2c-reset-password" /></div>
          <div>
            <Label>{t("mc.confirmPassword")}</Label>
            <PasswordInput required minLength={6} value={form.confirm || ""} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="b2c-reset-confirm" />
            {form.confirm && form.password !== form.confirm && (
              <p className="text-xs text-findo-red mt-1" data-testid="b2c-reset-mismatch">{t("mc.passwordMismatch")}</p>
            )}
          </div>
          <Button type="submit" disabled={busy} className="w-full h-11 rounded-xl font-semibold bg-findo-navy hover:bg-findo-navy/90" data-testid="b2c-reset-submit">{busy ? <FindoSpinner size={16} /> : t("mc.resetSave")}</Button>
        </form>
        <p className="mt-4 text-center text-sm text-slate-500">
          <button onClick={() => { setParams({}); setMode("login"); }} className="font-semibold text-findo-red" data-testid="b2c-reset-back">{t("mc.backToLogin")}</button>
        </p>
      </FormCard>
    );
  }

  if (mode === "recoversms") {
    return (
      <FormCard icon={Smartphone} title={t("mc2.lostEmailTitle")} className="max-w-md mx-auto" testid="b2c-recoversms-card">
        <p className="text-sm text-slate-500 mb-5">{t("mc2.recoverSmsDesc")}</p>
        {recoverMsg ? (
          <p className="text-sm font-semibold text-emerald-700" data-testid="b2c-recoversms-msg">{recoverMsg}</p>
        ) : (
          <form onSubmit={recoverBySms} className="space-y-6">
            <div><Label>{t("mc2.phoneNumber")}</Label><Input required value={recoverPhone} onChange={(e) => setRecoverPhone(e.target.value)} className="mt-1.5 h-11 rounded-xl" data-testid="b2c-recoversms-phone" /></div>
            <Button type="submit" disabled={busy} className="w-full h-11 rounded-xl font-semibold bg-findo-navy hover:bg-findo-navy/90" data-testid="b2c-recoversms-submit">{busy ? <FindoSpinner size={16} /> : t("mc2.recoverSmsBtn")}</Button>
          </form>
        )}
        <p className="mt-4 text-center text-sm text-slate-500">
          <button onClick={() => { setMode("login"); setRecoverMsg(""); }} className="font-semibold text-findo-red" data-testid="b2c-recoversms-back">{t("mc.backToLogin")}</button>
        </p>
      </FormCard>
    );
  }

  if (mode === "forgot") {
    return (
      <FormCard icon={KeyRound} title={t("mc.forgotTitle")} className="max-w-md mx-auto" testid="b2c-forgot-card">
        {forgotSent ? (
          <p className="mt-4 text-sm text-slate-600" data-testid="b2c-forgot-sent">{t("mc.forgotSent")}</p>
        ) : (
          <form onSubmit={submitForgot} className="space-y-6">
            <div><Label>{t("mc.email")}</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="b2c-forgot-email" /></div>
            <Button type="submit" disabled={busy} className="w-full h-11 rounded-xl font-semibold bg-findo-navy hover:bg-findo-navy/90" data-testid="b2c-forgot-submit">{busy ? <FindoSpinner size={16} /> : t("mc.forgotSend")}</Button>
          </form>
        )}
        <p className="mt-4 text-center text-sm text-slate-500">
          <button onClick={() => { setMode("login"); setForgotSent(false); }} className="font-semibold text-findo-red" data-testid="b2c-forgot-back">{t("mc.backToLogin")}</button>
        </p>
      </FormCard>
    );
  }

  return (
    <FormCard icon={mode === "register" ? UserPlus : LogIn} title={mode === "register" ? t("mc.registerTitle") : t("nav.login")} className="max-w-md mx-auto" bodyClassName="space-y-1" testid="b2c-auth-card">
      <span className="sr-only" data-testid="b2c-auth-title">{mode === "register" ? t("mc.registerTitle") : t("nav.login")}</span>
      <form onSubmit={submit} className="space-y-5">
        {mode === "register" && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("hc.firstName")} <span className="text-findo-red">*</span></Label><Input required autoComplete="given-name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="b2c-first-name" /></div>
              <div><Label>{t("hc.lastName")} <span className="text-findo-red">*</span></Label><Input required autoComplete="family-name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="b2c-last-name" /></div>
            </div>
            <div><Label>{t("mc.phone")}</Label><Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="b2c-phone" /></div>
            <div><Label>{t("mc.country")}</Label>
              <select value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="mt-1.5 h-11 w-full rounded-full border border-border px-3 text-sm" data-testid="b2c-country">
                {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{`${c.flag} ${c.name}`}</option>)}
              </select>
            </div>
            <FinderProfileFields form={form} setForm={setForm} onAvatar={onAvatar} t={t} />
          </>
        )}
        <div><Label>{t("mc.email")}</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="b2c-email" /></div>
        <div><Label>{t("mc.password")}</Label><PasswordInput required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="b2c-password" /></div>
        {mode === "register" && (
          <div>
            <Label>{t("mc.confirmPassword")}</Label>
            <PasswordInput required minLength={6} value={form.confirmReg || ""} onChange={(e) => setForm({ ...form, confirmReg: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="b2c-register-confirm" />
            {form.confirmReg && form.password !== form.confirmReg && (
              <p className="text-xs text-findo-red mt-1" data-testid="b2c-register-mismatch">{t("mc.passwordMismatch")}</p>
            )}
          </div>
        )}
        <div className="flex items-center justify-between gap-2">
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none" data-testid="b2c-remember-label">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="h-4 w-4 rounded border-border accent-findo-navy" data-testid="b2c-remember" />
            {t("mc.rememberMe")}
          </label>
          {mode === "login" && (
            <button type="button" onClick={() => setMode("forgot")} className="text-sm font-semibold text-findo-red" data-testid="b2c-forgot-link">{t("mc.forgotLink")}</button>
          )}
        </div>
        {mode === "login" && (
          <button type="button" onClick={() => { setMode("recoversms"); setRecoverMsg(""); }} className="w-full text-center text-sm font-semibold text-findo-navy hover:underline" data-testid="b2c-lost-email-link">
            {t("mc2.lostEmailTitle")}
          </button>
        )}
        {mode === "register" && (
          <label className="flex items-start gap-3 rounded-xl border border-border px-3 py-2.5 cursor-pointer" data-testid="b2c-cgu-label">
            <input type="checkbox" checked={cguReg} onChange={(e) => setCguReg(e.target.checked)} className="mt-0.5 accent-findo-navy h-4 w-4" data-testid="b2c-cgu-checkbox" />
            <span className="text-sm text-slate-600">
              {t("sub.cguPre")} <a href="/cgu" target="_blank" rel="noopener noreferrer" className="text-findo-navy font-semibold underline">{t("sub.cguLink")}</a> {t("sub.cguAnd")} <a href="/confidentialite" target="_blank" rel="noopener noreferrer" className="text-findo-navy font-semibold underline">{t("sub.privacyLink")}</a> {t("sub.cguSuffix")}
            </span>
          </label>
        )}
        <Button type="submit" disabled={busy || (mode === "register" && !cguReg)} className="w-full h-11 rounded-xl font-semibold bg-findo-navy hover:bg-findo-navy/90" data-testid="b2c-submit">
          {busy ? <FindoSpinner size={16} /> : mode === "login" ? t("mc.loginBtn") : t("mc.registerBtn")}
        </Button>
      </form>
      <RecaptchaNotice className="mt-3 text-center" />
      <p className="mt-4 text-center text-sm text-slate-500">
        {mode === "login" ? t("mc.toRegister") : t("mc.toLogin")}{" "}
        <button onClick={() => setMode(mode === "login" ? "register" : "login")} className="font-semibold text-findo-red" data-testid="b2c-switch-mode">
          {mode === "login" ? t("mc.registerBtn") : t("mc.loginBtn")}
        </button>
      </p>
    </FormCard>
  );
}

function Dashboard({ user, logout, t }) {
  const navigate = useNavigate();
  const { refreshUser } = useB2C();
  const [params] = useSearchParams();
  const renewId = params.get("renew");
  const [decls, setDecls] = useState(null);
  const [found, setFound] = useState([]);
  const [ads, setAds] = useState([]);
  const [adsPage, setAdsPage] = useState(1);
  const [foundAds, setFoundAds] = useState([]);
  const [matches, setMatches] = useState([]);
  const [finderContacts, setFinderContacts] = useState([]);
  const [claimedAds, setClaimedAds] = useState([]);
  useTicker();

  const reloadAds = () => api("/b2c/my-ads", { b2c: true }).then(setAds).catch(() => setAds([]));
  const hasLiveBoost = ads.some((a) => a.boost_until && new Date(a.boost_until) > new Date());
  useEffect(() => {
    if (!hasLiveBoost) return undefined;
    const poll = () => {
      if (document.hidden) return;
      api("/b2c/my-ads/boost-live", { b2c: true }).then((rows) => {
        const byId = Object.fromEntries((rows || []).map((r) => [r.id, r]));
        setAds((cur) => cur.map((a) => (byId[a.id] ? { ...a, ...byId[a.id] } : a)));
      }).catch(() => {});
    };
    const id = setInterval(poll, 15000);
    return () => clearInterval(id);
  }, [hasLiveBoost]);
  const reloadFound = () => api("/b2c/my-found", { b2c: true }).then(setFound).catch(() => setFound([]));
  const reloadFoundAds = () => api("/b2c/my-found-ads", { b2c: true }).then((r) => setFoundAds(r.items || [])).catch(() => setFoundAds([]));
  const reloadMatches = () => api("/b2c/my-matches", { b2c: true }).then((r) => setMatches(r.items || [])).catch(() => setMatches([]));
  const reloadFinderContacts = () => api("/b2c/my-finder-contacts", { b2c: true }).then((r) => setFinderContacts(Array.isArray(r) ? r : [])).catch(() => setFinderContacts([]));
  const reloadClaimedAds = () => api("/b2c/my-claimed-ads", { b2c: true }).then((r) => setClaimedAds(Array.isArray(r) ? r : [])).catch(() => setClaimedAds([]));
  const reloadDecls = () => api("/b2c/my-declarations", { b2c: true }).then(setDecls).catch(() => setDecls([]));

  useEffect(() => {
    reloadDecls();
    reloadFound();
    reloadAds();
    reloadFoundAds();
    reloadMatches();
    reloadFinderContacts();
    reloadClaimedAds();
  }, []);

  // Auto-refresh: reflect status changes done by partners (payment confirmed,
  // restitution, match) without a manual reload.
  useAutoRefresh(() => {
    reloadDecls();
    reloadMatches();
    reloadFinderContacts();
    reloadClaimedAds();
    reloadFoundAds();
  });

  async function downloadCertificate(url, name) {
    try {
      const res = await fetch(`${API}${url}`, { headers: { Authorization: `Bearer ${getB2CToken()}` } });
      if (!res.ok) throw new Error(t("mc2.error").replace("{status}", res.status));
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl; a.download = name || "certificat-findo.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(objUrl);
    } catch (e) { toast.error(e.message); }
  }

  async function downloadRecoveryReceipt(d) {
    try {
      const res = await fetch(`${API}/b2c/my-declarations/${d.id}/recovery-receipt.pdf`, {
        headers: { Authorization: `Bearer ${getB2CToken()}` },
      });
      if (!res.ok) throw new Error(t("mc2.error").replace("{status}", res.status));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `recu-recuperation-findloop-${d.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  }

  const ARCHIVED_STATUSES = ["closed", "resolved", "cancelled_by_customer"];
  const activeDecls = (decls || []).filter((d) => !ARCHIVED_STATUSES.includes(d.status));
  const archivedDecls = (decls || []).filter((d) => ARCHIVED_STATUSES.includes(d.status));
  const activeFound = (found || []).filter((f) => !["delivered", "closed"].includes(f.status));
  const archivedFound = (found || []).filter((f) => ["delivered", "closed"].includes(f.status));
  const activeFoundAds = (foundAds || []).filter((a) => !["closed", "resolved", "restituted"].includes(a.status));
  const archivedFoundAds = (foundAds || []).filter((a) => ["closed", "resolved", "restituted"].includes(a.status));
  const activeFinderContacts = (finderContacts || []).filter((c) => !["closed", "resolved", "restituted"].includes(c.status));
  const archivedFinderContacts = (finderContacts || []).filter((c) => ["closed", "resolved", "restituted"].includes(c.status));
  const activeClaimedAds = (claimedAds || []).filter((c) => !["closed", "resolved", "restituted"].includes(c.status));
  const activeAds = (ads || []).filter((a) => !["resolved", "closed", "restituted"].includes(a.status));
  const archivedAds = (ads || []).filter((a) => ["resolved", "closed", "restituted"].includes(a.status));

  return (
    <div data-testid="b2c-dashboard">
      <div className="flex items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-full findo-gradient grid place-items-center text-white"><UserRound size={22} /></div>
          <div>
            <p className="font-display text-xl font-semibold text-findo-navy">{t("mc.welcome")}, {user.name || user.email}</p>
            <p className="text-sm text-slate-500">{user.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <NotificationBell t={t} />
          <Button variant="outline" onClick={logout} className="rounded-full font-semibold border-findo-navy text-findo-navy" data-testid="b2c-logout">
            <LogOut size={16} className="mr-1.5" /> {t("mc.logout")}
          </Button>
        </div>
      </div>

      {/* Quick declare CTAs — moved to the very top for zero-scroll access */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6" data-testid="b2c-declare-cta">
        <button onClick={() => navigate("/declarer")} className="text-left bg-white rounded-2xl border border-border card-shadow p-5 flex items-center gap-4 hover:border-findo-red transition-colors" data-testid="cta-declare-lost">
          <div className="h-12 w-12 grid place-items-center rounded-xl bg-findo-red/10 text-findo-red shrink-0"><PackageSearch size={24} /></div>
          <div>
            <p className="font-display font-semibold text-findo-navy">{t("mc.declareLost")}</p>
            <p className="text-sm text-slate-500">{t("mc.declareLostSub")}</p>
          </div>
        </button>
        <button onClick={() => navigate("/annonces/trouvee")} className="text-left bg-white rounded-2xl border border-border card-shadow p-5 flex items-center gap-4 hover:border-teal-600 transition-colors" data-testid="cta-declare-found">
          <div className="h-12 w-12 grid place-items-center rounded-xl bg-teal-100 text-teal-700 shrink-0"><PackageCheck size={24} /></div>
          <div>
            <p className="font-display font-semibold text-findo-navy">{t("mc.declareFound")}</p>
            <p className="text-sm text-slate-500">{t("mc.declareFoundSub")}</p>
          </div>
        </button>
      </div>

      {/* Declarations (active only — resolved ones move to Historique) */}
      <Section title={t("mc.myDeclarations")} testid="b2c-declarations">
        {decls === null ? (
          <PageLoader label={t("c.loading")} className="py-8" size={32} />
        ) : activeDecls.length === 0 ? (
          <Empty icon={PackageSearch} text={t("mc.none")} />
        ) : (
          <div className="space-y-3" data-testid="b2c-declarations-list">
            {activeDecls.map((d) => (
              <DeclarationRow key={d.id} d={d} navigate={navigate} downloadRecoveryReceipt={downloadRecoveryReceipt} reload={reloadDecls} t={t} />
            ))}
          </div>
        )}
      </Section>

      {/* Finder score */}
      <FinderScoreCard user={user} t={t} />

      {/* Rewards */}
      <RewardsSection user={user} t={t} refreshUser={refreshUser} />

      {/* Points history */}
      <PointsHistorySection t={t} />

      {/* Objects the finder reported on public lost ads (attribution fix) */}
      <FinderContactsSection contacts={activeFinderContacts} navigate={navigate} t={t} />

      {/* Found ads this user is CLAIMING (they lost the object) */}
      <ClaimedAdsSection claimedAds={activeClaimedAds} navigate={navigate} reload={reloadClaimedAds} t={t} />

      {/* Cross-matches with one-click connect */}
      <MyMatchesSection matches={matches} navigate={navigate} t={t} reload={reloadMatches} />

      {/* Published found-object ads (active only) */}
      <MyFoundAdsSection foundAds={activeFoundAds} reload={reloadFoundAds} t={t} />

      {/* Lost-item public ads (active only — resolved move to Historique) */}
      <Section title={t("mc.myAds")} testid="b2c-ads">
        {activeAds.length === 0 ? (
          <Empty icon={PackageSearch} text={t("mc.adsNone")} />
        ) : (
          <div className="space-y-3" data-testid="b2c-ads-list">
            {paginate(activeAds, adsPage).pageItems.map((a) => <AdRow key={a.id} ad={a} t={t} reload={reloadAds} autoBoost={renewId === a.id} />)}
            <Pagination page={paginate(activeAds, adsPage).page} pages={paginate(activeAds, adsPage).pages} total={activeAds.length} onPage={setAdsPage} testid="b2c-ads-pagination" />
          </div>
        )}
      </Section>

      <ArchiveSection
        archivedDecls={archivedDecls}
        archivedFound={archivedFound}
        archivedFoundAds={archivedFoundAds}
        archivedFinderContacts={archivedFinderContacts}
        archivedAds={archivedAds}
        navigate={navigate}
        downloadRecoveryReceipt={downloadRecoveryReceipt}
        downloadCertificate={downloadCertificate}
        t={t}
      />

      <ProfileSettingsSection user={user} t={t} refreshUser={refreshUser} />

      <PrivacySection t={t} />
    </div>
  );
}

function DeclarationRow({ d, navigate, downloadRecoveryReceipt, reload, t, archived = false }) {
  const [cancelling, setCancelling] = React.useState(false);
  const [cancelOpen, setCancelOpen] = React.useState(false);
  const [closing, setClosing] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [shipModalOpen, setShipModalOpen] = React.useState(false);
  const CANCELLABLE = !["restituted", "delivered", "shipped", "closed", "resolved", "cancelled_by_customer"].includes(d.status);
  // Manual closure by the customer: the partner marks the object restituted, but
  // the B2C tracking card stays OPEN until the customer clicks « Clôturer ».
  const CLOSEABLE = !archived && ["restituted", "delivered", "shipped"].includes(d.status);
  async function cancelDeclaration(reason_code, reason_text) {
    setCancelling(true);
    try {
      await api(`/b2c/my-declarations/${d.id}/cancel`, { method: "POST", b2c: true, body: { reason_code, reason_text } });
      toast.success(t("mc2.cancelDeclDone") === "mc2.cancelDeclDone" ? "Déclaration annulée." : t("mc2.cancelDeclDone"));
      setCancelOpen(false);
      reload && reload();
    } catch (e) { toast.error(e.message); } finally { setCancelling(false); }
  }
  async function closeDeclaration() {
    setClosing(true);
    try {
      await api(`/b2c/my-declarations/${d.id}/close`, { method: "POST", b2c: true });
      toast.success(t("hc.caseClosedThankYou"));
      reload && reload();
    } catch (e) { toast.error(e.message); } finally { setClosing(false); }
  }
  return (
    <div className={`bg-white rounded-2xl border card-shadow p-5 ${archived ? "border-border opacity-90" : "border-border"}`} data-testid={archived ? "b2c-archived-row" : "b2c-declaration-row"}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-start justify-between gap-3 text-left" data-testid="b2c-declaration-toggle" aria-expanded={open}>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-findo-red font-semibold">{labelFor(d.category)}</p>
          <p className="text-base font-semibold text-slate-800 break-words leading-snug" data-testid="b2c-declaration-name">{d.description}</p>
          <p className="text-xs text-slate-400 mt-1 break-words">{t("mc.at")} {d.partner_name || "—"}</p>
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <div className="flex items-center gap-2">
            <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${statusCls(d.status)}`}>{t(`st.${d.status}`)}</span>
            <ChevronDown size={18} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
          </div>
          {!archived && d.awaiting_partner_confirmation && (
            <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 text-right" data-testid="b2c-awaiting-partner-badge">
              {t("hc.waitingForPartnerConfirmation")}
            </span>
          )}
          {!archived && d.payment_mode === "external" && d.is_recovery_paid && (
            <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-findo-indigo/10 text-findo-indigo text-right" data-testid="b2c-ext-paid-badge">
              {t("mc2.paidViaPartner")}
            </span>
          )}
        </div>
      </button>

      {open && (<>
      {!archived && d.claim_code && (
        <div className="mt-3 flex items-center justify-between gap-2 rounded-xl bg-findo-navy/5 border border-findo-navy/20 px-3 py-2.5" data-testid="b2c-claim-code">
          <span className="text-xs font-semibold text-slate-500 shrink-0">{t("mc2.claimCode")}</span>
          <span className="font-mono font-bold tracking-[0.2em] text-findo-navy text-sm break-all text-right">{d.claim_code}</span>
        </div>
      )}

      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid="b2c-declaration-actions">
        {!archived && d.awaiting_partner_confirmation ? (
          <button onClick={() => navigate(`/pay/${d.id}`)}
            className="flex flex-col items-center justify-center gap-1.5 rounded-xl border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 p-3 text-xs font-semibold text-center transition-colors" data-testid="b2c-resume-btn">
            <ArrowRight size={18} /> {t("hc.resume")}
          </button>
        ) : !archived && d.awaiting_recovery_payment ? (
          <button onClick={() => navigate(`/pay/${d.id}`)}
            className="flex flex-col items-center justify-center gap-1.5 rounded-xl border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 p-3 text-xs font-semibold text-center transition-colors" data-testid="b2c-pay-recovery-btn">
            <CreditCard size={18} /> {d.payment_mode === "external" ? t("mc2.paySettle") : t("mc2.payRecovery")}
          </button>
        ) : null}
        {!archived && ((d.is_shipping_paid || ["paid", "generating", "label_generated", "shipped", "delivered"].includes(d.shipment_status)) ? (
          <button disabled
            className="flex flex-col items-center justify-center gap-1.5 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 p-3 text-xs font-semibold text-center cursor-default" data-testid="b2c-pay-shipping-btn">
            <CheckCircle2 size={18} /> {t("hc.shippingPaid")}
          </button>
        ) : d.awaiting_shipping_payment ? (
          <button onClick={() => setShipModalOpen(true)}
            className="flex flex-col items-center justify-center gap-1.5 rounded-xl border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 p-3 text-xs font-semibold text-center transition-colors" data-testid="b2c-pay-shipping-btn">
            <Truck size={18} /> {t("mc2.payShipping")}
          </button>
        ) : null)}
        {d.recovery_receipt_available && (
          <button onClick={() => downloadRecoveryReceipt(d)}
            className="flex flex-col items-center justify-center gap-1.5 rounded-xl border border-findo-navy/20 bg-white text-findo-navy hover:bg-findo-navy/5 p-3 text-xs font-semibold text-center transition-colors" data-testid="b2c-recovery-receipt-btn">
            <Download size={18} /> {t("mc2.downloadReceipt")}
          </button>
        )}
        <button onClick={() => navigate(`/track/${d.id}`)}
          className="flex flex-col items-center justify-center gap-1.5 rounded-xl border border-findo-navy/20 bg-white text-findo-navy hover:bg-findo-navy/5 p-3 text-xs font-semibold text-center transition-colors" data-testid="b2c-track-btn">
          <ArrowRight size={18} /> {t("mc.track")}
        </button>
        {CLOSEABLE && reload && (
          <button onClick={closeDeclaration} disabled={closing}
            className="flex flex-col items-center justify-center gap-1.5 rounded-xl border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 p-3 text-xs font-semibold text-center transition-colors disabled:opacity-60" data-testid="b2c-close-declaration-btn">
            <CheckCircle2 size={18} /> {closing ? "…" : t("hc.close2")}
          </button>
        )}
        {!archived && CANCELLABLE && reload && (
          <button onClick={() => setCancelOpen(true)} disabled={cancelling}
            className="flex flex-col items-center justify-center gap-1.5 rounded-xl border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 p-3 text-xs font-semibold text-center transition-colors disabled:opacity-60" data-testid="b2c-cancel-declaration-btn">
            <XCircle size={18} /> {cancelling ? "…" : (t("mc2.cancelDecl") === "mc2.cancelDecl" ? "Annuler ma déclaration" : t("mc2.cancelDecl"))}
          </button>
        )}
      </div>
      </>)}

      <CancelReasonDialog open={cancelOpen} onOpenChange={setCancelOpen} loading={cancelling}
        onConfirm={cancelDeclaration}
        title={t("hc.cancelMyDeclaration")}
        description={t("hc.giveTheReasonForCancelling")} />
      <ShippingPayModal declarationId={d.id} open={shipModalOpen} onClose={() => setShipModalOpen(false)} />
    </div>
  );
}

const FINDER_STEPS = ["mc.stDeposited", "mc.stClaimed", "mc.stReturned"];

function FinderProgress({ stage, t, id }) {
  return (
    <div className="basis-full flex items-center gap-1.5 pt-1" aria-label={t("mc.progress")} data-testid={`finder-progress-${id}`}>
      {FINDER_STEPS.map((k, i) => {
        const done = i < stage;
        const current = i === stage - 1;
        return (
          <React.Fragment key={k}>
            {i > 0 && <span className={`h-0.5 flex-1 max-w-16 rounded ${done ? "bg-emerald-500" : "bg-slate-200"}`} />}
            <span className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1 ${done ? (current ? "bg-findo-navy text-white" : "bg-emerald-50 text-emerald-700") : "bg-slate-100 text-slate-400"}`}
              data-testid={`finder-progress-${id}-step-${i + 1}`} data-done={done}>
              {done && !current ? <CheckCircle2 size={12} /> : <span className="h-4 w-4 grid place-items-center text-[10px]">{i + 1}</span>} {t(k)}
            </span>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function FinderContactsSection({ contacts, navigate, t }) {
  return (
    <Section title={t("mc.myFinds")} testid="b2c-finder-contacts">
      <p className="text-sm text-slate-500 -mt-2 mb-4">{t("mc.myFindsSub")}</p>
      {contacts.length === 0 ? (
        <Empty icon={PackageSearch} text={t("mc.myFindsNone")} />
      ) : (
        <div className="space-y-3" data-testid="b2c-finder-contacts-list">
          {contacts.map((c) => (
            <div key={c.id} className="bg-white rounded-2xl border border-border card-shadow p-5 flex items-center gap-4 flex-wrap" data-testid="b2c-finder-contact-row">
              {c.image_base64 && <img src={c.image_base64} alt="" className="h-12 w-12 rounded-xl object-cover shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="text-xs text-teal-600 font-semibold">{labelFor(c.category)}</p>
                <p className="font-semibold text-slate-800 truncate">{c.description}</p>
                <p className="text-xs text-slate-400 mt-1">{t("an.lostAt")} {c.lost_place || "—"}</p>
              </div>
              {c.accepted && (
                <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-600" data-testid="finder-contact-accepted">
                  <CheckCircle2 size={13} /> {t("mc.finderAccepted")}
                </span>
              )}
              <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${statusCls(c.status)}`}>{t(`st.${c.status}`)}</span>
              <Button size="sm" variant="outline" onClick={() => navigate(`/suivi-trouve/${c.id}`)} className="rounded-full font-semibold" data-testid={`finder-contact-view-${c.id}`}>
                <Eye size={14} className="mr-1.5" /> {t("mc.track")}
              </Button>
              <FinderProgress stage={c.stage || 1} t={t} id={c.id} />
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

function ClaimedAdRow({ c, navigate, reload, t }) {
  const { lang } = useLang();
  const { user } = useB2C();
  const freeUnlock = isAfricaUser(user);
  const [unlocking, setUnlocking] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [step, setStep] = useState("confirm");
  const [cancelAsk, setCancelAsk] = useState(false);

  async function submitVerification(answers) {
    setVerifying(true);
    try {
      await api(`/b2c/ads/${c.id}/verify-claim`, { method: "POST", b2c: true, body: { answers } });
      setConfirmOpen(false);
      toast.success(t("hc.verificationSentToTheFinder"));
      reload?.();
    } catch (e) { toast.error(e.message); } finally { setVerifying(false); }
  }

  async function unlock() {
    setUnlocking(true);
    try {
      const res = await api(`/b2c/ads/${c.id}/unlock-contacts`, {
        method: "POST", b2c: true,
        body: { origin_url: window.location.origin, lang: localStorage.getItem("findo_lang") || "fr" },
      });
      if (res?.url) goToCheckout(res.url);
      else { toast.success(res?.message || t("mc2.unlockContacts")); reload?.(); }
    } catch (e) { toast.error(e.message); } finally { setUnlocking(false); }
  }

  async function decline(cancel = false) {
    setDeclining(true);
    try {
      await api(`/b2c/claimed-ads/${c.id}/decline`, { method: "POST", b2c: true });
      toast.success(t(cancel ? "mc.cancelClaimDone" : "mc.claimNotMineDone"));
      setConfirmOpen(false);
      reload?.();
    } catch (e) { toast.error(e.message); } finally { setDeclining(false); }
  }

  return (
    <div className="bg-white rounded-2xl border border-border card-shadow p-5" data-testid="b2c-claimed-row">
      <div className="flex items-center gap-4 flex-wrap">
        {c.image_base64 && <img src={c.image_base64} alt="" className="h-12 w-12 rounded-xl object-cover shrink-0" />}
        <div className="flex-1 min-w-0">
          <p className="text-xs text-teal-600 font-semibold">{labelFor(c.category)}</p>
          <p className="font-semibold text-slate-800 truncate">{c.description}</p>
          <p className="text-xs text-slate-400 mt-1">{t("an.lostAt")} {c.lost_place || "—"}</p>
        </div>
        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${statusCls(c.status)}`}>{t(`st.${c.status}`)}</span>
        <Button size="sm" variant="outline" onClick={() => navigate(`/suivi-trouve/${c.id}`)} className="rounded-full font-semibold" data-testid={`claimed-track-${c.id}`}>
          <Eye size={14} className="mr-1.5" /> {t("mc.track")}
        </Button>
      </div>

      {c.flow_v2 ? <ClaimV2Status c={c} t={t} /> : !c.contacts_unlocked ? (
        <div className="mt-3 border-t border-border pt-3 rounded-b-2xl" data-testid="claimed-unlock-block">
          {c.verification_status === "pending" ? (
            <WaitingNotice title={t("hc.verificationPending")} text={t("hc.theFinderIsReviewingYour")} testid={`claimed-verif-pending-${c.id}`} />
          ) : c.verification_status === "rejected" ? (
            <div className="rounded-xl bg-red-50 border border-red-200 p-3" data-testid={`claimed-verif-rejected-${c.id}`}>
              <p className="text-sm font-semibold text-red-700 flex items-center gap-1.5"><XCircle size={14} /> {t("hc.claimNotValidated")}</p>
              <p className="text-xs text-red-600 mt-1">{t("hc.theFinderConsideredThatThis")}</p>
            </div>
          ) : c.verification_status === "approved" ? (
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3" data-testid={`claimed-verif-approved-${c.id}`}>
              <p className="text-sm font-semibold text-emerald-800 flex items-center gap-1.5"><CheckCircle2 size={14} /> {t("hc.claimValidated")}</p>
              <p className="text-xs text-emerald-700 mt-1">{t("hc.unlockTheFinderSContact")}</p>
              <Button size="sm" onClick={unlock} disabled={unlocking} className="mt-2.5 rounded-full font-semibold bg-findo-navy hover:bg-findo-navy/90" data-testid={`claimed-unlock-btn-${c.id}`}>
                {unlocking ? "…" : (freeUnlock ? <>{t("mc2.unlockContacts")} · 🎉 {FREE_UNLOCK_LABEL}</> : <>{t("mc2.unlockContacts")} · {unlockFeeLabel(c.country, lang)}</>)}
              </Button>
            </div>
          ) : (
            <div className="rounded-xl bg-findo-navy/[0.04] border border-findo-navy/15 p-3">
              <p className="text-sm font-semibold text-findo-navy flex items-center gap-1.5"><Lock size={14} /> {t("mc.claimUnlockTitle")}</p>
              <p className="text-xs text-slate-500 mt-1">{t("hc.confirmThatThisIsYour")}</p>
              <div className="mt-2.5 flex flex-wrap items-center gap-2">
                <Button size="sm" onClick={() => { setLightbox(false); setStep("confirm"); setConfirmOpen(true); }} className="rounded-full font-semibold bg-findo-navy hover:bg-findo-navy/90" data-testid={`claimed-unlock-btn-${c.id}`}>
                  {t("hc.thisIsMyItem")}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setCancelAsk(true)} disabled={declining} className="rounded-full font-semibold text-slate-600" data-testid={`claimed-cancel-btn-${c.id}`}>
                  {declining ? "…" : t("mc.cancelClaim")}
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : c.finder ? (
        <div className="mt-3 border-t border-border pt-3" data-testid="claimed-finder-contacts">
          <p className="text-xs font-semibold text-findo-navy inline-flex items-center gap-1.5 mb-2"><UserRound size={14} /> {t("mc.claimFinderContacts")}</p>
          <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-sm">
            <p className="font-semibold text-slate-800 flex items-center gap-1.5"><User size={13} /> {c.finder.name}</p>
            {c.finder.email && <p className="text-slate-600 flex items-center gap-1.5 mt-1"><Mail size={13} /> <a href={`mailto:${c.finder.email}`} className="hover:text-findo-navy">{c.finder.email}</a></p>}
            {c.finder.phone && <p className="text-slate-600 flex items-center gap-1.5 mt-0.5"><Phone size={13} /> <a href={`tel:${c.finder.phone}`} className="hover:text-findo-navy">{c.finder.phone}</a></p>}
          </div>
        </div>
      ) : null}

      <Dialog open={cancelAsk} onOpenChange={setCancelAsk}>
        <DialogContent className="sm:max-w-sm text-center" aria-describedby={undefined} data-testid="cancel-claim-dialog">
          <DialogTitle className="font-display text-lg text-findo-navy text-center">{t("mc.cancelClaimConfirm")}</DialogTitle>
          <div className="mt-2 flex gap-2 justify-center">
            <Button variant="outline" onClick={() => setCancelAsk(false)} className="rounded-full font-semibold" data-testid="cancel-claim-no">{t("mc.cancel")}</Button>
            <Button onClick={() => { setCancelAsk(false); decline(true); }} className="rounded-full font-semibold bg-findo-danger hover:bg-findo-danger/90" data-testid="cancel-claim-yes">{t("mc.confirm")}</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={confirmOpen} onOpenChange={(o) => { if (!o) { setConfirmOpen(false); setLightbox(false); } }}>
        <DialogContent className="sm:max-w-md max-h-[95vh] overflow-y-auto" aria-describedby={undefined} data-testid="claim-confirm-dialog" onCloseAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-findo-navy">{step === "confirm" ? t("mc.claimConfirmTitle") : t("fr.proveTitle")}</DialogTitle>
          </DialogHeader>
          {step === "confirm" ? (<>
          <div className="mt-1 flex gap-4 rounded-2xl border border-border bg-slate-50 p-4">
            {c.image_base64 ? (
              <button type="button" onClick={() => setLightbox(true)}
                className="h-24 w-24 rounded-xl overflow-hidden shrink-0 cursor-zoom-in group relative focus:outline-none focus:ring-2 focus:ring-findo-navy"
                data-testid="claim-confirm-photo-btn" aria-label={t("hc.enlargePhoto")}>
                <img src={c.image_base64} alt="" className="h-full w-full object-cover transition-transform group-hover:scale-105" data-testid="claim-confirm-photo" />
                <span className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-colors grid place-items-center">
                  <Eye size={20} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </span>
              </button>
            ) : (
              <div className="h-24 w-24 rounded-xl bg-slate-200 grid place-items-center text-slate-400 shrink-0"><PackageSearch size={28} /></div>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-xs text-teal-600 font-semibold">{labelFor(c.category)}</p>
              <p className="font-semibold text-slate-800 break-words" data-testid="claim-confirm-desc">{c.description}</p>
              <p className="text-xs text-slate-500 mt-1.5 flex items-center gap-1"><MapPin size={12} /> {c.lost_place || "—"}</p>
              {c.lost_date && <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1"><CalendarClock size={12} /> {c.lost_date}</p>}
            </div>
          </div>
          <div className="mt-3 flex flex-col gap-2">
            <Button onClick={() => setStep("verify")} className="rounded-full font-semibold bg-findo-navy hover:bg-findo-navy/90" data-testid="claim-confirm-yes-btn">
              {t("fr.yesMine")}
            </Button>
            <Button variant="outline" onClick={() => decline()} disabled={declining} className="rounded-full font-semibold text-slate-600" data-testid="claim-confirm-no-btn">
              {declining ? "…" : t("mc.claimConfirmNo")}
            </Button>
          </div>
          </>) : (
            <VerificationAnswersForm questions={c.verification_questions} busy={verifying}
              onSubmit={submitVerification} onCancel={() => setStep("confirm")} />
          )}
          <PhotoLightbox src={c.image_base64} open={lightbox} onClose={() => setLightbox(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ClaimV2Status({ c, t }) {
  const st = c.contacts_unlocked ? "unlocked" : c.review_status || "pending";
  if (st === "pending") return <div className="mt-3"><WaitingNotice title={t("fa.waitFinderDecision")} text={t("fa.waitClaimText")} testid={`claimed-v2-pending-${c.id}`} /></div>;
  const cls = { approved: "text-emerald-700", unlocked: "text-emerald-700", rejected: "text-slate-500" }[st];
  const key = { approved: "fa.claimApprovedShort", unlocked: "fa.claimUnlockedShort", rejected: "fa.claimRejectedShort" }[st];
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <p className={`text-sm font-semibold ${cls}`} data-testid={`claimed-v2-${st}-${c.id}`}>{t(key)}</p>
      {st === "approved" && (
        <a href={`/suivi-trouve/${c.id}`} className="rounded-full bg-findo-navy text-white text-xs font-semibold px-3 py-1.5 hover:bg-findo-navy/90" data-testid={`claimed-v2-unlock-${c.id}`}>{t("fa.unlockBtn")}</a>
      )}
    </div>
  );
}

function ClaimedAdsSection({ claimedAds, navigate, reload, t }) {
  if (!claimedAds || claimedAds.length === 0) return null;
  return (
    <Section title={t("mc.myClaims")} testid="b2c-claimed-ads">
      <p className="text-sm text-slate-500 -mt-2 mb-4">{t("mc.myClaimsSub")}</p>
      <div className="space-y-3" data-testid="b2c-claimed-list">
        {claimedAds.map((c) => (
          <ClaimedAdRow key={c.id} c={c} navigate={navigate} reload={reload} t={t} />
        ))}
      </div>
    </Section>
  );
}


function HistoryRow({ item, t }) {
  return (
    <div className="bg-white rounded-2xl border border-border card-shadow p-5 flex items-center gap-4 flex-wrap opacity-95" data-testid="b2c-history-row">
      {item.image && <img src={item.image} alt="" className="h-11 w-11 rounded-xl object-cover shrink-0" />}
      <div className="flex-1 min-w-0">
        <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide">{item.kindLabel}</p>
        <p className="font-semibold text-slate-800 truncate" data-testid="history-item-name">{item.description}</p>
        <div className="mt-1 flex items-center gap-2 flex-wrap">
          {item.org && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-teal-700 bg-teal-50 rounded-full px-2 py-0.5" data-testid="history-item-org">
              <Store size={11} /> {item.org}
            </span>
          )}
          {item.place && <span className="text-xs text-slate-400">{item.place}</span>}
        </div>
      </div>
      <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${statusCls(item.status)}`}>{t(`st.${item.status}`)}</span>
      {item.onReceipt && (
        <Button onClick={item.onReceipt} size="sm" variant="outline" className="rounded-full font-semibold border-findo-navy/30 text-findo-navy hover:bg-findo-navy/5" data-testid="history-receipt-btn">
          <Download size={15} className="mr-1" /> {t("mc2.downloadReceipt")}
        </Button>
      )}
      {item.onCertificate && (
        <Button onClick={item.onCertificate} size="sm" variant="outline" className="rounded-full font-semibold border-teal-600/30 text-teal-700 hover:bg-teal-50" data-testid="history-certificate-btn">
          <Download size={15} className="mr-1" /> {t("mc.certificate")}
        </Button>
      )}
    </div>
  );
}

function ArchiveSection({ archivedDecls, archivedFound, archivedFoundAds, archivedFinderContacts, archivedAds, navigate, downloadRecoveryReceipt, downloadCertificate, t }) {
  const [open, setOpen] = useState(false);

  const items = [
    ...(archivedDecls || []).map((d) => ({
      key: `d-${d.id}`, kindLabel: t("mc.hDeclaration"), description: d.description,
      place: d.lost_place, org: d.partner_name, status: d.status, image: null,
      onReceipt: d.recovery_receipt_available ? () => downloadRecoveryReceipt(d) : null,
      onCertificate: null,
    })),
    ...(archivedFound || []).map((f) => ({
      key: `f-${f.id}`, kindLabel: t("mc.hFound"), description: f.description,
      place: f.found_place, org: f.partner_name, status: f.status, image: f.image_base64 || null,
      onReceipt: null,
      onCertificate: () => downloadCertificate(`/b2c/my-found/${f.id}/certificate.pdf`, `certificat-restitution-${f.id.slice(0, 8)}.pdf`),
    })),
    ...(archivedFoundAds || []).map((a) => ({
      key: `fa-${a.id}`, kindLabel: t("mc.hFoundAd"), description: a.description,
      place: a.lost_place, org: null, status: a.status, image: a.image_base64 || null,
      onReceipt: null,
      onCertificate: () => downloadCertificate(`/b2c/found-ads/${a.id}/certificate.pdf`, `certificat-restitution-${a.id.slice(0, 8)}.pdf`),
    })),
    ...(archivedAds || []).map((a) => ({
      key: `a-${a.id}`, kindLabel: t("mc.hAd"), description: a.description,
      place: a.lost_place, org: null, status: a.status, image: a.image_base64 || null,
      onReceipt: null, onCertificate: null,
    })),
    ...(archivedFinderContacts || []).map((c) => ({
      key: `fc-${c.id}`, kindLabel: t("mc.hFinderContact"), description: c.description,
      place: c.lost_place, org: null, status: c.status, image: c.image_base64 || null,
      onReceipt: null, onCertificate: null,
    })),
  ];

  return (
    <Section title={t("mc.archiveTitle")} testid="b2c-archive">
      <div className="flex items-center justify-between gap-3 -mt-2 mb-4 flex-wrap">
        <p className="text-sm text-slate-500">{t("mc.archiveSub")}</p>
        {items.length > 0 && (
          <Button size="sm" variant="outline" onClick={() => setOpen((v) => !v)} className="rounded-full font-semibold" data-testid="b2c-archive-toggle">
            {open ? t("mc.archiveHide") : `${t("mc.archiveShow")} (${items.length})`}
          </Button>
        )}
      </div>
      {items.length === 0 ? (
        <Empty icon={PackageCheck} text={t("mc.archiveNone")} />
      ) : open ? (
        <div className="space-y-3" data-testid="b2c-archive-list">
          {items.map((it) => <HistoryRow key={it.key} item={it} t={t} />)}
        </div>
      ) : null}
    </Section>
  );
}

function PrivacySection({ t }) {
  const { lang } = useLang();
  const { logout } = useB2C();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activity, setActivity] = useState(null);
  const [params, setParams] = useSearchParams();

  const loadActivity = () => api("/b2c/me/activity", { b2c: true }).then(setActivity).catch(() => {});
  useEffect(() => { loadActivity(); }, []);

  useEffect(() => {
    if (params.get("reactivate") === "1") {
      api("/b2c/me/reactivate", { method: "POST", b2c: true })
        .then(() => { toast.success(t("mc2.reactivatedThanks")); loadActivity(); })
        .catch(() => {})
        .finally(() => { params.delete("reactivate"); setParams(params, { replace: true }); });
    }
  }, []); // eslint-disable-line

  async function reactivate() {
    try { await api("/b2c/me/reactivate", { method: "POST", b2c: true }); toast.success(t("mc2.reactivated")); loadActivity(); }
    catch (e) { toast.error(e.message); }
  }

  async function exportData() {
    setBusy(true);
    try {
      const res = await fetch(`${API}/b2c/gdpr/export`, {
        headers: { Authorization: `Bearer ${getB2CToken()}` },
      });
      if (!res.ok) throw new Error(t("mc2.error").replace("{status}", res.status));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "findloop-mes-donnees.json";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t("mc.exportDone"));
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }

  async function eraseAccount() {
    setDeleting(true);
    try {
      await api("/b2c/gdpr/erase", { method: "POST", b2c: true });
      toast.success(t("mc.deleted"));
      logout();
      navigate("/");
    } catch (e) { toast.error(e.message); setDeleting(false); }
  }

  return (
    <Section title={t("mc.privacy")} testid="b2c-privacy">
      {activity && (
        <div className={`bg-white rounded-2xl border card-shadow p-5 flex items-center gap-4 mb-3 ${activity.at_risk ? "border-amber-300" : "border-border"}`} data-testid="b2c-activity-card">
          <div className={`h-11 w-11 rounded-full grid place-items-center shrink-0 ${activity.at_risk ? "bg-amber-50 text-amber-600" : "bg-findo-cream text-findo-navy"}`}>
            <CalendarClock size={20} />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-slate-800">{t("mc2.accountActivity")}</p>
            <p className="text-xs text-slate-500">
              {t("mc2.lastActivity")} <b>{activity.last_active_at ? new Date(activity.last_active_at).toLocaleDateString(lang) : "—"}</b>
              {" · "}{t("mc2.autoDelete")} <b>{new Date(activity.scheduled_deletion_at).toLocaleDateString(lang)}</b> ({t("mc2.daysShort").replace("{n}", activity.days_until_deletion)})
              {t("mc2.inactivityNote").replace("{n}", activity.inactivity_months)}
            </p>
          </div>
          <Button onClick={reactivate} variant="outline" className="rounded-full font-semibold shrink-0" data-testid="b2c-reactivate-btn">
            {t("mc2.reactivateAccount")}
          </Button>
        </div>
      )}
      <div className="bg-white rounded-2xl border border-border card-shadow p-5 flex items-center gap-4">
        <div className="h-11 w-11 rounded-full bg-findo-cream grid place-items-center text-findo-navy shrink-0">
          <ShieldCheck size={20} />
        </div>
        <p className="flex-1 text-sm text-slate-500">{t("mc.privacyDesc")}</p>
        <Button onClick={exportData} disabled={busy} variant="outline" className="rounded-full font-semibold border-findo-navy text-findo-navy shrink-0" data-testid="b2c-export-data-btn">
          <Download size={16} className="mr-1.5" /> {busy ? <span className="inline-flex items-center gap-1.5"><FindoSpinner size={16} />{t("mc.exporting")}</span> : t("mc.exportData")}
        </Button>
      </div>

      <div className="mt-3 bg-white rounded-2xl border border-red-200 card-shadow p-5 flex items-center gap-4">
        <div className="h-11 w-11 rounded-full bg-red-50 grid place-items-center text-findo-danger shrink-0">
          <Trash2 size={20} />
        </div>
        <p className="flex-1 text-sm text-slate-500">{t("mc.deleteDesc")}</p>
        <Button onClick={() => setConfirmOpen(true)} variant="ghost" className="rounded-full font-semibold text-findo-danger hover:bg-red-50 shrink-0" data-testid="b2c-delete-account-btn">
          <Trash2 size={16} className="mr-1.5" /> {t("mc.deleteAccount")}
        </Button>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="b2c-delete-dialog" onCloseAutoFocus={(e) => e.preventDefault()}>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("mc.deleteConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("mc.deleteConfirmBody")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="b2c-delete-cancel">{t("mc.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); eraseAccount(); }} disabled={deleting} className="bg-findo-danger hover:bg-findo-danger/90" data-testid="b2c-delete-confirm">
              {deleting ? t("mc.deleting") : t("mc.deleteConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Section>
  );
}

function MyMatchesSection({ matches, navigate, t, reload }) {
  if (!matches || matches.length === 0) return null;
  return (
    <Section title={t("mc.matches")} testid="b2c-matches">
      <p className="text-sm text-slate-500 -mt-2 mb-4">{t("mc.matchesSub")}</p>
      <div className="space-y-4" data-testid="b2c-matches-list">
        {matches.map((m) => (
          <div key={m.ad_id} className="bg-white rounded-2xl border border-emerald-200 card-shadow p-5" data-testid="b2c-match-group">
            <p className="text-xs text-slate-400">{t("mc.forYourAd")}</p>
            <p className="font-semibold text-findo-navy truncate">{labelFor(m.ad_category)} · {m.ad_description}</p>
            <div className="mt-3 space-y-2">
              {m.candidates.map((c) => (
                <MatchCandidateRow key={c.id} c={c} adId={m.ad_id} navigate={navigate} t={t} reload={reload} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function MatchCandidateRow({ c, adId, navigate, t, reload }) {
  const [cancelOpen, setCancelOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState(null);

  async function submitCancel(reason_code, reason_text) {
    setBusy(true);
    try {
      const r = await api("/b2c/matches/cancel", { method: "POST", b2c: true, body: { ad_id: adId, item_id: c.id, reason_code, reason_text } });
      setReason(r.reason || "");
      setCancelOpen(false);
      toast.success(t("hc.reasonSavedYouCanStill"));
      reload && reload();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }

  return (
    <div className="flex items-center gap-3 flex-wrap rounded-xl bg-emerald-50/60 border border-emerald-100 p-3" data-testid="b2c-match-candidate">
      <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-600 text-white">
        {c.type === "b2b" ? t("mc.matchPartner") : t("mc.matchPublic")}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-800 truncate">{labelFor(c.category)} — {c.description}</p>
        <p className="text-xs text-slate-400">{c.place || "—"}{c.date ? ` · ${c.date}` : ""}</p>
        {reason && <p className="text-xs text-amber-600 mt-1" data-testid="match-cancel-reason">{t("hc.cancelSavedFmt").replace("{reason}", reason)}</p>}
      </div>
      <Button size="sm" onClick={() => navigate(c.type === "b2b" ? `/objet-trouve/${c.id}?ad=${encodeURIComponent(adId || "")}` : c.link)} className="rounded-full font-semibold bg-emerald-600 hover:bg-emerald-700" data-testid={`match-connect-${c.id}`}>
        <Handshake size={14} className="mr-1.5" /> {t("mc.connect")}
      </Button>
      {c.type === "b2b" && (
        <Button size="sm" variant="ghost" onClick={() => setCancelOpen(true)} disabled={busy}
          className="rounded-full font-semibold text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`match-cancel-${c.id}`}>
          <XCircle size={14} className="mr-1" /> {t("hc.cancel")}
        </Button>
      )}
      <CancelReasonDialog open={cancelOpen} onOpenChange={setCancelOpen} loading={busy}
        onConfirm={submitCancel}
        title={t("hc.cancelThisMatch")}
        description={t("hc.tellUsWhyYourListing")} />
    </div>
  );
}

function MyFoundAdsSection({ foundAds, reload, t }) {
  const navigate = useNavigate();
  const [busyId, setBusyId] = useState(null);
  const activeCount = foundAds.filter((a) => a.status === "open").length;

  async function close(ad) {
    if (!window.confirm(t("mc.closeAdConfirm"))) return;
    setBusyId(ad.id);
    try {
      await api(`/b2c/found-ads/${ad.id}/close`, { method: "POST", b2c: true });
      toast.success(t("mc.adClosed"));
      reload();
    } catch (e) { toast.error(e.message); } finally { setBusyId(null); }
  }

  return (
    <Section title={t("mc.myFoundAds")} testid="b2c-found-ads">
      <div className="flex items-center justify-between gap-3 -mt-2 mb-4 flex-wrap">
        <p className="text-sm text-slate-500" data-testid="found-ads-count">{t("mc2.adsActive").replace("{n}", activeCount)}</p>
        <Button size="sm" onClick={() => navigate("/annonces/trouvee")} className="rounded-full font-semibold bg-teal-600 hover:bg-teal-600/90" data-testid="found-ads-new-btn">
          <Sparkles size={14} className="mr-1.5" /> {t("anf.post")}
        </Button>
      </div>
      {foundAds.length === 0 ? (
        <Empty icon={PackageSearch} text={t("mc.foundAdsNone")} />
      ) : (
        <div className="space-y-3" data-testid="b2c-found-ads-list">
          {foundAds.map((a) => {
            const closed = a.status === "closed" || a.status === "resolved" || a.status === "restituted";
            return (
              <div key={a.id} className="bg-white rounded-2xl border border-border card-shadow p-5 flex items-center gap-4 flex-wrap" data-testid="b2c-found-ad-row">
                {a.image_base64 && <img src={a.image_base64} alt="" className="h-12 w-12 rounded-xl object-cover shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-teal-600 font-semibold">{labelFor(a.category)}</p>
                  <p className="font-semibold text-slate-800 truncate">{a.description}</p>
                  <p className="text-xs text-slate-400 mt-1">{t("anf.foundAt")} {a.lost_place || "—"}</p>
                </div>
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${a.points_state === "awarded" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid={`found-ad-points-${a.id}`}>
                  <Trophy size={11} className="inline mr-1" />{a.points_state === "awarded" ? t("mc.pointsOk") : t("mc.pointsAwaiting")}
                </span>
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${statusCls(a.status)}`}>{t(`st.${a.status}`)}</span>
                <Button size="sm" variant="outline" onClick={() => navigate(`/suivi-trouve/${a.id}`)} className="rounded-full font-semibold" data-testid={`found-ad-view-${a.id}`}>
                  <Eye size={14} className="mr-1.5" /> {t("mc.track")}
                </Button>
                {!closed && (
                  <Button size="sm" variant="ghost" onClick={() => close(a)} disabled={busyId === a.id} className="rounded-full font-semibold text-slate-500" data-testid={`found-ad-close-${a.id}`}>
                    <X size={14} className="mr-1.5" /> {t("mc.closeAd")}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Section>
  );
}

function isBoosted(ad) {
  return ad.boost_until && new Date(ad.boost_until).getTime() > Date.now();
}

function AdStatsChart({ ad, t }) {
  const boosted = isBoosted(ad);
  const data = [
    { name: t("mc.views"), value: ad.view_count || 0, fill: "#1e3a5f" },
    { name: t("mc.contacts"), value: ad.contact_count || 0, fill: "#e11d48" },
  ];
  if (boosted) {
    data.push({ name: t("mc.boostViews"), value: ad.boost_views || 0, fill: "#3b82f6" });
    data.push({ name: t("mc.boostContacts"), value: ad.boost_contacts || 0, fill: "#f97316" });
  }
  const allZero = data.every((d) => d.value === 0);
  return (
    <div className="mt-3 border-t border-border pt-3" data-testid="ad-stats-chart">
      <p className="text-xs font-semibold text-findo-navy inline-flex items-center gap-1.5 mb-2">
        <BarChart3 size={14} /> {t("mc.statsTitle")}
      </p>
      {allZero ? (
        <p className="text-xs text-slate-400">—</p>
      ) : (
        <div style={{ width: "100%", height: 120 }}>
          <ResponsiveContainer>
            <BarChart data={data} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748b" }} interval={0} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#94a3b8" }} width={28} />
              <Tooltip cursor={{ fill: "rgba(0,0,0,0.04)" }} contentStyle={{ fontSize: 12, borderRadius: 12 }} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]} maxBarSize={44}>
                {data.map((d, i) => <Cell key={i} fill={d.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function AdRow({ ad, t, reload, autoBoost }) {
  const { lang } = useLang();
  const { user } = useB2C();
  const freeUnlock = isAfricaUser(user);
  const [boostOpen, setBoostOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [decliningFinders, setDecliningFinders] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const boosted = isBoosted(ad);
  const closed = ad.status === "resolved" || ad.status === "closed" || ad.status === "restituted";
  // A finder has been accepted (status "Trouveur accepté — code envoyé par email")
  // → hide Modifier & Renouveler le boost; only Clôturer stays available.
  const finderAccepted = !!ad.accepted_contact_id;
  // The B2B partner handed the object back in person: keep the ad OPEN, only the
  // owner closes it manually once received.
  const partnerRestituted = !!ad.partner_restituted;
  const lockActions = finderAccepted || partnerRestituted || ad.status === "pending_payment";

  async function unlockContacts() {
    setUnlocking(true);
    try {
      const res = await api(`/b2c/ads/${ad.id}/unlock-contacts`, { method: "POST", b2c: true, body: { origin_url: window.location.origin, lang: localStorage.getItem("findo_lang") || "fr" } });
      if (res?.url) { goToCheckout(res.url); return; }
      // Free (African launch offer) or already applied → reveal + confirm.
      if (res?.free || res?.ok || res?.message) { toast.success(res?.message || t("mc2.unlockContacts")); reload?.(); return; }
      toast.error(t("mc2.payUnavailable"));
    } catch (e) { toast.error(e.message); } finally { setUnlocking(false); }
  }

  async function declineFinders() {
    setDecliningFinders(true);
    try {
      await api(`/b2c/ads/${ad.id}/decline-finders`, { method: "POST", b2c: true });
      setConfirmOpen(false);
      toast.success(t("hc.reportDismissedYourListingRemains"));
      reload?.();
    } catch (e) { toast.error(e.message); } finally { setDecliningFinders(false); }
  }

  const [downloadingReceipt, setDownloadingReceipt] = useState(false);
  async function downloadReceipt() {
    setDownloadingReceipt(true);
    try {
      const res = await fetch(`${API}/b2c/ads/${ad.id}/unlock-receipt.pdf`, {
        headers: { Authorization: `Bearer ${getB2CToken()}` },
      });
      if (!res.ok) throw new Error(t("mc2.error").replace("{status}", res.status));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `recu-findloop-${ad.unlock_receipt_number || ad.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); } finally { setDownloadingReceipt(false); }
  }

  useEffect(() => {
    if (autoBoost && !closed && !ad.flow_v2) setBoostOpen(true);
  }, [autoBoost, closed, ad.flow_v2]);

  async function close(restituted) {
    setCloseOpen(false);
    setBusy(true);
    try {
      await api(`/b2c/ads/${ad.id}/close?restituted=${restituted ? "true" : "false"}`, { method: "POST", b2c: true });
      toast.success(
        restituted
          ? (t("ad.closedRestituted") === "ad.closedRestituted" ? "Objet restitué — annonce clôturée 🎉" : t("ad.closedRestituted"))
          : t("ad.closed"),
      );
      reload();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }

  return (
    <div className={`bg-white rounded-2xl border card-shadow p-5 ${boosted ? "border-findo-red ring-1 ring-findo-red/30" : "border-border"}`} data-testid="b2c-ad-row">
      {ad.status === "hidden" && (
        <div className="mb-3 rounded-xl border border-amber-300 bg-amber-50 px-3.5 py-2.5 text-sm text-amber-900" data-testid="ad-hidden-notice">
          <span className="font-bold">{t("hc.listingHiddenByModeration")}</span>{" "}
          {ad.hidden_reason || "Cette annonce n'est plus visible publiquement. Contactez le support pour en savoir plus."}
        </div>
      )}
      <div className="flex items-center gap-4">
        {ad.image_base64 && <img src={ad.image_base64} alt="" className="h-12 w-12 rounded-xl object-cover shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-xs text-findo-red font-semibold">{labelFor(ad.category)}</p>
            {boosted && (
              <span className="inline-flex items-center gap-1 boost-shine rounded-full bg-findo-red text-white px-2 py-0.5 text-[11px] font-bold" data-testid="ad-boost-badge">
                <Sparkles size={11} /> {t("an.boosted")} · {formatRemaining(ad.boost_until, t)}
              </span>
            )}
          </div>
          <p className="font-semibold text-slate-800 truncate">{ad.description}</p>
          <p className="text-xs text-slate-400 mt-1">
            {t("mc.lostAt")} {ad.lost_place || "—"}
          </p>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-500" data-testid="ad-stats">
            {boosted ? <LiveBoostViews value={ad.boost_views || 0} /> : <span className="inline-flex items-center gap-1"><Eye size={13} /> {ad.view_count || 0}</span>}
            <span className="inline-flex items-center gap-1"><Mail size={13} /> {boosted ? (ad.boost_contacts || 0) : (ad.contact_count || 0)}</span>
            {boosted && <span className="text-findo-red font-semibold">· {t("bo.since")}</span>}
          </div>
        </div>
        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${statusCls(ad.status)}`}>{t(`st.${ad.status}`)}</span>
      </div>

      {!boosted && isRecentlyExpired(ad.boost_until) && !closed && !finderAccepted && (
        <div className="mt-4 rounded-xl border-2 border-findo-red bg-gradient-to-r from-findo-red/10 to-findo-red/5 p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="ad-boost-winback">
          <span className="h-9 w-9 rounded-lg bg-findo-red grid place-items-center text-white shrink-0 font-extrabold text-xs">-20%</span>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-findo-navy text-sm">{t("mc2.boostExpiredWin")}</p>
            <p className="text-xs text-slate-600 mt-0.5">
              {t("mc2.boostEarnedWin")} <b className="text-findo-red">{ad.boost_views || 0} {t("mc2.viewsWord")}</b>{t("mc2.boostWinDetail2")}
            </p>
          </div>
          <Button size="sm" onClick={() => setBoostOpen(true)} className="rounded-full font-semibold bg-findo-red hover:bg-findo-red/90 shrink-0" data-testid="ad-boost-winback-btn">
            <RefreshCw size={14} className="mr-1.5" /> {t("mc2.resume20")}
          </Button>
        </div>
      )}

      {boosted && isExpiringSoon(ad.boost_until) && !closed && !finderAccepted && (
        <div className="mt-4 rounded-xl border-2 border-findo-red bg-findo-red/5 p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="ad-boost-renew">
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-findo-navy text-sm flex items-center gap-1.5">
              <RefreshCw size={15} className="text-findo-red" /> {t("mc2.boostExpiresIn")} {formatRemaining(ad.boost_until, t)}
            </p>
            <p className="text-xs text-slate-600 mt-0.5">
              {t("mc2.boostEarnedAlready")} <b className="text-findo-red">{ad.boost_views || 0} {t("mc2.viewsWord")}</b>{t("mc2.renewStayTop")}
            </p>
          </div>
          <Button size="sm" onClick={() => setBoostOpen(true)} className="rounded-full font-semibold bg-findo-red hover:bg-findo-red/90 shrink-0" data-testid="ad-boost-renew-btn">
            <RefreshCw size={14} className="mr-1.5" /> {t("bo.renew")}
          </Button>
        </div>
      )}

      {!closed && (
        <div className="mt-4 border-t border-border pt-3">
          {partnerRestituted ? (
            <div className="mb-2.5 flex items-start gap-2 rounded-lg bg-findo-indigo/5 border border-findo-indigo/20 px-3 py-2 text-xs text-findo-indigo" data-testid="ad-partner-restituted-notice">
              <PackageCheck size={14} className="shrink-0 mt-0.5" />
              <span>
                {ad.partner_restituted_mode === "shipping"
                  ? t("hc.yourItemHasBeenShipped")
                  : t("hc.yourItemHasBeenHanded")}
              </span>
            </div>
          ) : finderAccepted && (
            <div className="mb-2.5 flex items-start gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-800" data-testid="ad-finder-accepted-notice">
              <CheckCircle2 size={14} className="shrink-0 mt-0.5 text-emerald-600" />
              <span>{t("hc.finderAcceptedCloseTheCase")}</span>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {!lockActions && (
              <>
                <Button size="sm" onClick={() => setBoostOpen(true)} className="rounded-full font-semibold bg-findo-red hover:bg-findo-red/90" data-testid="ad-boost-btn">
                  <Sparkles size={14} className="mr-1.5" /> {boosted ? t("bo.renew") : t("ad.boost")}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setEditOpen(true)} className="rounded-full font-semibold" data-testid="ad-edit-btn">
                  <Pencil size={14} className="mr-1.5" /> {t("ad.edit")}
                </Button>
              </>
            )}
            <Button size="sm" variant="ghost" onClick={() => setCloseOpen(true)} disabled={busy} className="rounded-full font-semibold text-slate-500" data-testid="ad-close-btn">
              <X size={14} className="mr-1.5" /> {t("ad.close")}
            </Button>
          </div>
        </div>
      )}

      <AdStatsChart ad={ad} t={t} />

      {ad.flow_v2 && <LostResponsesReview ad={ad} reload={reload} autoRenew={autoBoost} />}

      {ad.pending_contact_count > 0 && (
        ad.contacts_unlocked ? (
          <div className="mt-3 border-t border-border pt-3" data-testid="ad-finder-contacts">
            <p className="text-xs font-semibold text-findo-navy inline-flex items-center gap-1.5 mb-2">
              <UserRound size={14} /> {t("mc2.finderContacts")}
            </p>
            <div className="space-y-2">
              {(ad.contacts || []).map((c) => (
                <div key={c.id} className="rounded-xl bg-slate-50 border border-border p-3 text-sm" data-testid={`finder-contact-${c.id}`}>
                  <p className="font-semibold text-slate-800 flex items-center gap-1.5"><User size={13} /> {c.finder_name}</p>
                  <p className="text-slate-600 flex items-center gap-1.5 mt-1"><Mail size={13} /> <a href={`mailto:${c.finder_email}`} className="hover:text-findo-navy">{c.finder_email}</a></p>
                  <p className="text-slate-600 flex items-center gap-1.5 mt-0.5"><Phone size={13} /> <a href={`tel:${c.finder_phone}`} className="hover:text-findo-navy">{c.finder_phone}</a></p>
                  {c.message && <p className="text-slate-500 italic mt-1.5">« {c.message} »</p>}
                  <AcceptFinderButton ad={ad} contact={c} reload={reload} />
                </div>
              ))}
            </div>
            <Button size="sm" variant="outline" onClick={downloadReceipt} disabled={downloadingReceipt}
              className="mt-3 rounded-full font-semibold border-findo-navy/30 text-findo-navy hover:bg-findo-navy/5" data-testid="ad-receipt-btn">
              <Download size={14} className="mr-1.5" /> {downloadingReceipt ? "…" : t("mc2.downloadReceipt")}
            </Button>
          </div>
        ) : ad.finder_reports?.length ? (
          <FinderReportsReview ad={ad} reload={reload} onDecline={declineFinders} declining={decliningFinders}
            onUnlock={unlockContacts} unlocking={unlocking}
            unlockLabel={freeUnlock ? <>{t("mc2.unlockContacts")} · 🎉 {FREE_UNLOCK_LABEL}</> : <>{t("mc2.unlockContacts")} · {unlockFeeLabel(ad.country, lang)}</>} />
        ) : (
          <div className="mt-3 border-t border-border pt-3 rounded-b-2xl" data-testid="ad-unlock-block">
            <div className="rounded-xl bg-findo-navy/[0.04] border border-findo-navy/15 p-3">
              <p className="text-sm font-semibold text-findo-navy flex items-center gap-1.5">
                <Lock size={14} /> {t("mc2.peopleFound").replace("{n}", ad.pending_contact_count)}
              </p>
              <p className="text-xs text-slate-500 mt-1">{t("mc2.unlockDesc")}</p>
              <Button size="sm" onClick={() => { setLightbox(false); setConfirmOpen(true); }} disabled={unlocking} className="mt-2.5 rounded-full font-semibold bg-findo-navy hover:bg-findo-navy/90" data-testid="ad-unlock-btn">
                {unlocking ? "…" : (freeUnlock ? <>{t("mc2.unlockContacts")} · 🎉 {FREE_UNLOCK_LABEL}</> : <>{t("mc2.unlockContacts")} · {unlockFeeLabel(ad.country, lang)}</>)}
              </Button>
            </div>
          </div>
        )
      )}

      <Dialog open={confirmOpen} onOpenChange={(o) => { if (!o) { setConfirmOpen(false); setLightbox(false); } }}>
        <DialogContent className="sm:max-w-md" data-testid="ad-claim-confirm-dialog" onCloseAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-findo-navy">{t("hc.isThisReallyYourItem")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 -mt-1">{t("hc.makeSureThisIsThe")}</p>
          <div className="mt-2 flex gap-4 rounded-2xl border border-border bg-slate-50 p-4">
            {ad.image_base64 ? (
              <button type="button" onClick={() => setLightbox(true)}
                className="h-24 w-24 rounded-xl overflow-hidden shrink-0 cursor-zoom-in group relative focus:outline-none focus:ring-2 focus:ring-findo-navy"
                data-testid="ad-claim-confirm-photo-btn" aria-label={t("hc.enlargePhoto")}>
                <img src={ad.image_base64} alt="" className="h-full w-full object-cover transition-transform group-hover:scale-105" data-testid="ad-claim-confirm-photo" />
                <span className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-colors grid place-items-center">
                  <Eye size={20} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </span>
              </button>
            ) : (
              <div className="h-24 w-24 rounded-xl bg-slate-200 grid place-items-center text-slate-400 shrink-0"><PackageSearch size={28} /></div>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-xs text-findo-red font-semibold">{labelFor(ad.category)}</p>
              <p className="font-semibold text-slate-800 break-words" data-testid="ad-claim-confirm-desc">{ad.description}</p>
              <p className="text-xs text-slate-500 mt-1.5 flex items-center gap-1"><MapPin size={12} /> {ad.lost_place || "—"}</p>
              {ad.lost_date && <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1"><CalendarClock size={12} /> {ad.lost_date}</p>}
            </div>
          </div>
          <div className="mt-3 flex flex-col gap-2">
            <Button onClick={() => { setConfirmOpen(false); unlockContacts(); }} disabled={unlocking || decliningFinders}
              className="rounded-full font-semibold bg-findo-navy hover:bg-findo-navy/90" data-testid="ad-claim-confirm-yes-btn">
              {freeUnlock ? <>Oui, c'est mon objet · 🎉 {FREE_UNLOCK_LABEL}</> : <>Oui, c'est mon objet · {unlockFeeLabel(ad.country, lang)}</>}
            </Button>
            <Button onClick={declineFinders} disabled={decliningFinders || unlocking} variant="outline"
              className="rounded-full font-semibold text-slate-600" data-testid="ad-claim-confirm-no-btn">
              {decliningFinders ? "…" : t("hc.thisIsNotMyItem")}
            </Button>
          </div>
          <PhotoLightbox src={ad.image_base64} open={lightbox} onClose={() => setLightbox(false)} />
        </DialogContent>
      </Dialog>

      <BoostAdDialog adId={ad.id} open={boostOpen} onClose={() => setBoostOpen(false)} renew={boosted} country={ad.country} />
      <EditAdDialog ad={ad} open={editOpen} onClose={() => setEditOpen(false)} t={t} reload={reload} />

      <AlertDialog open={closeOpen} onOpenChange={setCloseOpen}>
        <AlertDialogContent data-testid="ad-close-dialog" onCloseAutoFocus={(e) => e.preventDefault()}>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("ad.closeQuestion") === "ad.closeQuestion" ? "L'objet a-t-il été restitué ?" : t("ad.closeQuestion")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("ad.closeQuestionDesc") === "ad.closeQuestionDesc"
                ? t("hc.specifyWhetherYouRecoveredYour")
                : t("ad.closeQuestionDesc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-col gap-2">
            <Button
              onClick={() => close(true)} disabled={busy}
              className="w-full rounded-full font-semibold bg-emerald-600 hover:bg-emerald-700"
              data-testid="ad-close-restituted-btn">
              <CheckCircle2 size={16} className="mr-1.5" />
              {t("ad.closeYesRestituted") === "ad.closeYesRestituted" ? "Oui, l'objet a été restitué" : t("ad.closeYesRestituted")}
            </Button>
            <Button
              onClick={() => close(false)} disabled={busy} variant="outline"
              className="w-full rounded-full font-semibold"
              data-testid="ad-close-cancel-btn">
              <X size={16} className="mr-1.5" />
              {t("ad.closeNoCancel") === "ad.closeNoCancel" ? "Non, retirer l'annonce" : t("ad.closeNoCancel")}
            </Button>
            <AlertDialogCancel className="w-full rounded-full mt-0" data-testid="ad-close-abort-btn">
              {t("mc.cancel") === "mc.cancel" ? "Annuler" : t("mc.cancel")}
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function AcceptFinderButton({ ad, contact, reload }) {
  const { t } = useLang();
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState(null);
  const accepted = ad.accepted_contact_id === contact.id;

  async function accept() {
    setBusy(true);
    try {
      const res = await api(`/b2c/ads/${ad.id}/accept-finder`, { method: "POST", b2c: true, body: { contact_id: contact.id } });
      setCode(res?.code || null);
      toast.success(t("mc2.codeSentFinder"));
      reload?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }

  if (accepted || code) {
    return (
      <div className="mt-2.5 rounded-lg bg-emerald-50 border border-emerald-200 p-2.5" data-testid={`finder-accepted-${contact.id}`}>
        <p className="text-xs font-semibold text-emerald-700 flex items-center gap-1.5"><CheckCircle2 size={13} /> {t("mc2.finderAccepted")}</p>
        {code && <p className="mt-1 text-sm font-bold tracking-[0.25em] font-mono text-findo-navy text-center bg-white rounded border border-emerald-200 py-1.5" data-testid="finder-code-value">{code}</p>}
      </div>
    );
  }
  return (
    <Button size="sm" onClick={accept} disabled={busy} className="mt-2.5 rounded-full font-semibold bg-emerald-600 hover:bg-emerald-700" data-testid={`finder-accept-btn-${contact.id}`}>
      <CheckCircle2 size={14} className="mr-1.5" /> {busy ? <FindoSpinner size={16} /> : t("mc2.acceptFinder")}
    </Button>
  );
}

function EditAdDialog({ ad, open, onClose, t, reload }) {
  const [form, setForm] = useState({ description: ad.description, lost_place: ad.lost_place, category: ad.category,
    ...(ad.flow_v2 ? { city: ad.city || "", circumstances: ad.circumstances || "", color: ad.color || "", condition: ad.condition || "", brand: ad.brand || "" } : {}) });
  const [busy, setBusy] = useState(false);

  async function save(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api(`/b2c/ads/${ad.id}`, { method: "PATCH", b2c: true, body: { ...form } });
      toast.success(t("ad.saved"));
      onClose();
      reload();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="edit-ad-dialog" onCloseAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader><DialogTitle className="font-display">{t("ad.editTitle")}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="findo-numbered space-y-5">
          <div><Label>{t("dec.category")}</Label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1.5 h-11 w-full rounded-full border border-border px-3 text-sm" data-testid="edit-ad-category">
              {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          <div><Label>{t("dec.description")}</Label><Textarea required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 rounded-xl" data-testid="edit-ad-description" /></div>
          <div><Label>{t("an.lostAt")}</Label><Input required value={form.lost_place} onChange={(e) => setForm({ ...form, lost_place: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="edit-ad-place" /></div>
          {ad.flow_v2 && (
            <div className="grid grid-cols-2 gap-3">
              {[["city", "fa.fCity"], ["color", "fa.fColor"], ["condition", "fa.fCondition"], ["brand", "fa.fBrand"]].map(([k, l]) => (
                <div key={k}><Label>{t(l)}</Label><Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid={`edit-ad-${k}`} /></div>
              ))}
              <div className="col-span-2"><Label>{t("fa.fCircumstances")}</Label><Textarea value={form.circumstances} onChange={(e) => setForm({ ...form, circumstances: e.target.value })} className="mt-1.5 rounded-xl" data-testid="edit-ad-circumstances" /></div>
            </div>
          )}
          <Button type="submit" disabled={busy} className="w-full rounded-xl font-semibold bg-findo-navy hover:bg-findo-navy/90" data-testid="edit-ad-save">{busy ? <FindoSpinner size={16} /> : t("c.save")}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const BADGE_META = {
  bronze: { labelKey: "mc2.badgeBronze", cls: "bg-amber-700/20 text-amber-200 border-amber-600/40" },
  silver: { labelKey: "mc2.badgeSilver", cls: "bg-slate-300/20 text-slate-100 border-slate-300/40" },
  gold: { labelKey: "mc2.badgeGold", cls: "bg-amber-400/20 text-amber-200 border-amber-300/50" },
};

function NotificationBell({ t }) {
  const { lang } = useLang();
  const navigate = useNavigate();
  const [data, setData] = useState({ notifications: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const [cancelledAds, setCancelledAds] = useState(() => new Set());
  const [cancelling, setCancelling] = useState(null);

  const load = () => api("/b2c/notifications", { b2c: true }).then(setData).catch(() => {});
  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  async function cancelFoundAd(adId) {
    setCancelling(adId);
    try {
      await api(`/b2c/found-ads/${adId}/cancel`, { method: "POST", b2c: true });
      setCancelledAds((s) => new Set(s).add(adId));
      toast.success(t("mc.notifAdCancelled"));
      window.dispatchEvent(new Event("findloop:ads-changed"));
    } catch (e) { toast.error(e.message); } finally { setCancelling(null); }
  }

  async function openChange(o) {
    setOpen(o);
    if (o && data.unread > 0) {
      try { await api("/b2c/notifications/read-all", { method: "POST", b2c: true }); load(); } catch { /* ignore */ }
    }
  }

  function linkFor(n) {
    const m = n.meta || {};
    if (m.resolved) return null;
    if (m.match === "b2b" && m.item_id) return `/objet-trouve/${m.item_id}?ad=${encodeURIComponent(m.ad_id || "")}`;
    if (m.link) return m.link;
    if (m.ad_id) return `/annonces/${m.ad_id}?from=notif`;
    if (m.declaration_id) return `/track/${m.declaration_id}`;
    return null;
  }

  function ctaLabel(n) {
    const m = n.meta || {};
    if (m.match === "b2b") return t("hc.viewItemRecover");
    if (m.link) return t("mc.notifOpen");
    if (m.ad_id) return t("mc.notifViewAd");
    if (m.declaration_id) return t("mc.notifTrack");
    return t("mc.notifOpen");
  }

  async function onClickNotif(n) {
    const m = n.meta || {};
    setOpen(false);
    // B2B match: check whether restitution is already underway → go to the
    // object's tracking card instead of restarting the recovery flow.
    if (m.match === "b2b" && m.item_id && !m.resolved) {
      try {
        const r = await api(`/b2c/b2b-match/${m.item_id}/resume?ad=${encodeURIComponent(m.ad_id || "")}`, { b2c: true });
        if (r?.route) { navigate(r.route); return; }
      } catch { /* fall back to the default link below */ }
    }
    const link = linkFor(n);
    if (!link) return;
    navigate(link);
  }

  async function downloadCert(url, name) {
    try {
      const res = await fetch(`${API}${url}`, { headers: { Authorization: `Bearer ${getB2CToken()}` } });
      if (!res.ok) throw new Error(t("mc2.error").replace("{status}", res.status));
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl; a.download = name || "certificat-findo.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(objUrl);
    } catch (e) { toast.error(e.message); }
  }

  const items = data.notifications || [];
  return (
    <Popover open={open} onOpenChange={openChange}>
      <PopoverTrigger asChild>
        <button className="relative h-11 w-11 rounded-full border border-findo-navy/20 grid place-items-center hover:bg-findo-navy/5 transition-colors" data-testid="notif-bell" aria-label="Notifications">
          <Bell size={18} className="text-findo-navy" />
          {data.unread > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-findo-red text-white text-[10px] font-bold grid place-items-center" data-testid="notif-badge">
              {data.unread > 9 ? "9+" : data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notif-panel">
        <div className="px-4 py-3 border-b border-border flex items-center gap-2">
          <Bell size={15} className="text-findo-navy" />
          <p className="font-semibold text-findo-navy text-sm">{t("mc.notifTitle")}</p>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 ? (
            <p className="text-center text-slate-400 text-sm py-10" data-testid="notif-empty">{t("mc.notifEmpty")}</p>
          ) : (
            items.map((n) => {
              const link = linkFor(n);
              return (
              <div key={n.id} onClick={() => onClickNotif(n)}
                className={`px-4 py-3 border-b border-border last:border-0 ${n.read ? "" : "bg-findo-cream/40"} ${link ? "cursor-pointer hover:bg-findo-navy/[0.04] transition-colors" : ""}`}
                data-testid={`notif-item-${n.id}`}>
                <div className="flex items-start gap-2.5">
                  <span className={`h-8 w-8 rounded-full grid place-items-center shrink-0 ${n.type === "voucher_expiring" ? "bg-findo-danger/10 text-findo-danger" : n.type === "verification_rejected" ? "bg-red-100 text-red-600" : (n.meta?.resolved || n.type === "verification_approved") ? "bg-emerald-100 text-emerald-600" : (n.type === "ad_published" || n.type === "declaration_created" || n.type === "shipment_shipped" || n.type === "shipping_payment_due" || n.type === "b2b_match" || n.type === "finder_declined" || n.type === "verification_pending") ? "bg-findo-indigo/10 text-findo-indigo" : "bg-emerald-100 text-emerald-600"}`}>
                    {n.type === "voucher_expiring" ? <Ticket size={15} /> : (n.type === "verification_pending" || n.type === "verification_approved" || n.type === "verification_rejected") ? <ShieldCheck size={15} /> : (n.meta?.resolved) ? <CheckCircle2 size={15} /> : (n.type === "shipment_shipped" || n.type === "shipping_payment_due") ? <Truck size={15} /> : (n.type === "ad_published" || n.type === "declaration_created" || n.type === "b2b_match" || n.type === "finder_declined") ? <PackageSearch size={15} /> : <Trophy size={15} />}
                  </span>
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-800 text-sm">{n.title}</p>
                    {n.body && <p className="text-xs text-slate-500 mt-0.5">{n.body}</p>}
                    <p className="text-[10px] text-slate-400 mt-1">{n.created_at ? new Date(n.created_at).toLocaleString(lang) : ""}</p>
                    {link && <p className="text-[11px] font-semibold text-findo-indigo mt-1" data-testid={`notif-cta-${n.id}`}>{ctaLabel(n)} →</p>}
                    {n.meta?.certificate_url && (
                      <button type="button" onClick={(e) => { e.stopPropagation(); downloadCert(n.meta.certificate_url, n.meta.certificate_name); }}
                        className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-teal-600 text-white text-[11px] font-bold px-3 py-1.5 hover:bg-teal-700"
                        data-testid={`notif-receipt-${n.id}`}>
                        <Download size={12} /> {t("mc.certificate")}
                      </button>
                    )}
                    {n.meta?.action === "cancel_found_ad" && n.meta?.ad_id && (
                      cancelledAds.has(n.meta.ad_id) ? (
                        <p className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-500" data-testid={`notif-ad-cancelled-${n.id}`}>
                          <CheckCircle2 size={12} className="text-emerald-600" /> {t("mc.notifAdCancelled")}
                        </p>
                      ) : (
                        <button type="button" onClick={(e) => { e.stopPropagation(); cancelFoundAd(n.meta.ad_id); }}
                          disabled={cancelling === n.meta.ad_id}
                          className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-findo-red text-white text-[11px] font-bold px-3 py-1.5 hover:bg-findo-red/90 disabled:opacity-60"
                          data-testid={`notif-cancel-ad-${n.id}`}>
                          <X size={12} /> {cancelling === n.meta.ad_id ? "…" : t("mc.notifCancelAd")}
                        </button>
                      )
                    )}
                  </div>
                </div>
              </div>
            );})
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function FinderScoreCard({ user, t }) {
  const navigate = useNavigate();
  const badge = user.finder_badge ? BADGE_META[user.finder_badge] : null;
  return (
    <div className="mb-10" data-testid="finder-score-card">
      <div className="rounded-2xl overflow-hidden border border-border card-shadow bg-gradient-to-br from-findo-navy to-[#0f1e3a] text-white p-6 flex items-center gap-5 flex-wrap">
        <div className="h-16 w-16 rounded-2xl bg-white/10 grid place-items-center shrink-0">
          <Trophy size={30} className="text-amber-300" />
        </div>
        <div className="flex-1 min-w-[180px]">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm text-white/70 font-semibold uppercase tracking-wide">{t("mc.scoreTitle")}</p>
            {badge && <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full border ${badge.cls}`} data-testid="finder-badge"><Award size={11} /> {t(badge.labelKey)}</span>}
          </div>
          <p className="font-display text-4xl font-semibold mt-0.5" data-testid="finder-score-value">
            {user.finder_score || 0} <span className="text-lg text-white/60">{t("mc.scorePts")}</span>
          </p>
          <p className="text-xs text-white/60 mt-1">{t("mc.scoreThisYear")} : {user.finder_score_year || 0} · {t("mc.pointsBalance")} : <b className="text-amber-300">{user.points_balance || 0}</b></p>
        </div>
        <Button onClick={() => navigate("/classement")} variant="outline" className="rounded-full font-semibold bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0" data-testid="score-leaderboard-btn">
          {t("mc.seeRanking")}
        </Button>
      </div>
    </div>
  );
}

function ConfirmRestitutionButton({ foundId, t, reload, refreshUser }) {
  const [busy, setBusy] = useState(false);
  async function confirm() {
    setBusy(true);
    try {
      const res = await api(`/b2c/found/${foundId}/confirm-restitution`, { method: "POST", b2c: true });
      if (res.awarded) toast.success(t("mc.scoreAwarded"));
      else toast.success(t("mc.restitutionConfirmed"));
      await reload();
      await refreshUser();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }
  return (
    <Button size="sm" onClick={confirm} disabled={busy} className="rounded-full font-semibold bg-emerald-600 hover:bg-emerald-700" data-testid={`confirm-restitution-${foundId}`}>
      <CheckCircle2 size={15} className="mr-1.5" /> {busy ? <FindoSpinner size={16} /> : t("mc.confirmRestitution")}
    </Button>
  );
}

function VisibilityToggles({ form, setForm, t }) {
  const rows = [
    ["show_photo", t("mc.visPhoto")],
    ["show_name", t("mc.visName")],
    ["show_city", t("mc.visCity")],
    ["show_country", t("mc.visCountry")],
  ];
  return (
    <div className="mt-3 grid grid-cols-2 gap-2" data-testid="visibility-toggles">
      {rows.map(([k, label]) => (
        <label key={k} className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none rounded-xl border border-border px-3 py-2">
          <input type="checkbox" checked={!!form[k]} onChange={(e) => setForm((s) => ({ ...s, [k]: e.target.checked }))}
            className="h-4 w-4 rounded border-border accent-findo-navy" data-testid={`vis-${k}`} />
          {label}
        </label>
      ))}
    </div>
  );
}

function FinderProfileFields({ form, setForm, onAvatar, onRemoveAvatar, onPickPreset, uploading, t }) {
  const avatarSrc = form.avatar_url
    ? `${process.env.REACT_APP_BACKEND_URL}${form.avatar_url}`
    : form.avatar_base64;
  return (
    <div className="rounded-2xl border border-border bg-findo-cream/40 p-4" data-testid="finder-profile-fields">
      <p className="text-sm font-bold text-findo-navy flex items-center gap-1.5"><Trophy size={15} className="text-amber-500" /> {t("mc.finderProfile")}</p>
      <p className="text-xs text-slate-500 mt-0.5">{t("mc.finderProfileDesc")}</p>
      <div className="mt-3 flex items-center gap-3">
        <div className="h-14 w-14 rounded-full overflow-hidden bg-white border border-border grid place-items-center shrink-0" data-testid="avatar-preview">
          {avatarSrc ? <AvatarImg src={avatarSrc} alt="" className="h-full w-full object-cover" /> : <Camera size={20} className="text-slate-300" />}
        </div>
        <label className="text-xs font-semibold text-findo-navy border border-findo-navy/30 rounded-full px-3 py-1.5 cursor-pointer hover:bg-findo-navy/5" data-testid="avatar-upload-label">
          {uploading ? <FindoSpinner size={16} /> : (avatarSrc ? t("mc.changePhoto") : t("mc.addPhoto"))}
          <input type="file" accept="image/*" className="hidden" onChange={onAvatar} disabled={uploading} data-testid="avatar-upload" />
        </label>
        {avatarSrc && (
          <button type="button" onClick={() => (onRemoveAvatar ? onRemoveAvatar() : setForm((s) => ({ ...s, avatar_base64: null })))} className="text-xs text-findo-danger font-semibold" data-testid="avatar-remove">{t("mc.remove")}</button>
        )}
      </div>
      {!avatarSrc && (
        <div className="mt-3" data-testid="avatar-presets">
          <p className="text-xs text-slate-500 mb-2">{t("mc2.noPhotoPreset")}</p>
          <div className="flex flex-wrap gap-2">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((i) => (
              <button key={i} type="button" onClick={() => onPickPreset?.(`p${i}`)} disabled={uploading}
                className="h-11 w-11 rounded-full overflow-hidden border border-border hover:ring-2 hover:ring-findo-red transition disabled:opacity-50" data-testid={`avatar-preset-p${i}`}>
                <img src={`/avatars/preset-${i}.jpg`} alt={`Avatar ${i}`} className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="mt-3 grid grid-cols-2 gap-2">
        <div><Label className="text-xs">{t("mc.pseudo")}</Label><Input value={form.pseudo || ""} onChange={(e) => setForm((s) => ({ ...s, pseudo: e.target.value }))} className="mt-1 h-10 rounded-xl" data-testid="b2c-pseudo" /></div>
        <div><Label className="text-xs">{t("mc.city")}</Label><Input value={form.city || ""} onChange={(e) => setForm((s) => ({ ...s, city: e.target.value }))} className="mt-1 h-10 rounded-xl" data-testid="b2c-city" /></div>
      </div>
      <label className="mt-3 flex items-start gap-2 text-sm text-slate-700 cursor-pointer select-none rounded-xl bg-white border border-border p-3" data-testid="leaderboard-optin-label">
        <input type="checkbox" checked={!!form.leaderboard_opt_in} onChange={(e) => setForm((s) => ({ ...s, leaderboard_opt_in: e.target.checked }))}
          className="h-4 w-4 mt-0.5 rounded border-border accent-findo-navy" data-testid="b2c-leaderboard-optin" />
        <span><b>{t("mc.optInTitle")}</b><br /><span className="text-xs text-slate-500">{t("mc.optInDesc")}</span></span>
      </label>
      {form.leaderboard_opt_in && (
        <>
          <p className="mt-3 text-xs font-semibold text-findo-navy">{t("mc.visLabel")}</p>
          <VisibilityToggles form={form} setForm={setForm} t={t} />
        </>
      )}
    </div>
  );
}

function ProfileSettingsSection({ user, t, refreshUser }) {
  const [form, setForm] = useState({
    pseudo: user.pseudo || "", city: user.city || "",
    avatar_url: user.avatar_url || null, avatar_base64: user.avatar_base64 || null,
    leaderboard_opt_in: !!user.leaderboard_opt_in, show_photo: !!user.show_photo,
    show_name: !!user.show_name, show_city: !!user.show_city, show_country: !!user.show_country,
  });
  const [busy, setBusy] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);

  async function onAvatar(e) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setCropSrc(reader.result);
    reader.readAsDataURL(f);
  }

  async function onCropConfirm(file) {
    setUploadingAvatar(true);
    try {
      const res = await uploadFile("/b2c/me/avatar", file, { b2c: true });
      setForm((s) => ({ ...s, avatar_url: res.avatar_url, avatar_base64: null }));
      setCropSrc(null);
      toast.success(t("mc.photoUpdated"));
      await refreshUser();
    } catch (err) { toast.error(err.message); } finally { setUploadingAvatar(false); }
  }

  async function removeAvatar() {
    setUploadingAvatar(true);
    try {
      await api("/b2c/me/avatar", { method: "DELETE", b2c: true });
      setForm((s) => ({ ...s, avatar_url: null, avatar_base64: null }));
      await refreshUser();
    } catch (err) { toast.error(err.message); } finally { setUploadingAvatar(false); }
  }

  async function pickPreset(preset) {
    setUploadingAvatar(true);
    try {
      const res = await api("/b2c/me/avatar/preset", { method: "POST", b2c: true, body: { preset } });
      setForm((s) => ({ ...s, avatar_url: res.avatar_url, avatar_base64: null }));
      toast.success(t("mc.photoUpdated"));
      await refreshUser();
    } catch (err) { toast.error(err.message); } finally { setUploadingAvatar(false); }
  }

  async function save() {
    setBusy(true);
    try {
      const body = {
        pseudo: form.pseudo, city: form.city,
        leaderboard_opt_in: form.leaderboard_opt_in, show_photo: form.show_photo,
        show_name: form.show_name, show_city: form.show_city, show_country: form.show_country,
      };
      await api("/b2c/profile", { method: "PATCH", b2c: true, body });
      toast.success(t("mc.profileSaved"));
      await refreshUser();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }

  return (
    <Section title={t("mc.profileSettings")} testid="b2c-profile-settings">
      <div className="bg-white rounded-2xl border border-border card-shadow p-5">
        <FinderProfileFields form={form} setForm={setForm} onAvatar={onAvatar} onRemoveAvatar={removeAvatar} onPickPreset={pickPreset} uploading={uploadingAvatar} t={t} />
        <Button onClick={save} disabled={busy} className="mt-4 rounded-full font-semibold bg-findo-navy hover:bg-findo-navy/90" data-testid="profile-save-btn">
          {busy ? <FindoSpinner size={16} /> : t("c.save")}
        </Button>
      </div>
      <AvatarCropDialog open={!!cropSrc} imageSrc={cropSrc} onCancel={() => setCropSrc(null)} onConfirm={onCropConfirm} />
    </Section>
  );
}

function RewardCard({ r, user, t, busyId, onRedeem }) {
  const affordable = (user.points_balance || 0) >= r.points_cost;
  return (
    <div className="bg-white rounded-2xl border border-border card-shadow p-4 flex flex-col" data-testid={`b2c-reward-${r.id}`}>
      <div className="flex items-start gap-2">
        <div className="h-10 w-10 rounded-xl bg-findo-cream border border-border grid place-items-center overflow-hidden shrink-0">
          {r.partner_logo_base64 ? <img src={r.partner_logo_base64} alt="" className="h-full w-full object-cover" /> : <Gift size={18} className="text-findo-indigo" />}
        </div>
        <div className="min-w-0">
          <p className="font-semibold text-slate-800 leading-tight">{r.title}</p>
          <p className="text-[11px] text-slate-400 truncate">{r.partner_name}{r.partner_city ? ` · ${r.partner_city}` : ""}</p>
        </div>
      </div>
      {r.description && <p className="text-xs text-slate-500 mt-2 flex-1">{r.description}</p>}
      <div className="flex items-center justify-between gap-2 mt-3">
        <span className="inline-flex items-center gap-1 text-sm font-bold text-findo-indigo"><Coins size={14} /> {r.points_cost} {t("mc.scorePts")}</span>
        <Button size="sm" disabled={!affordable || busyId === r.id} onClick={() => onRedeem(r)} className="rounded-full font-semibold bg-findo-navy hover:bg-findo-navy/90 disabled:opacity-50" data-testid={`redeem-${r.id}`}>
          {busyId === r.id ? "…" : affordable ? t("mc.redeem") : t("mc.notEnough")}
        </Button>
      </div>
    </div>
  );
}

function RewardsSection({ user, t, refreshUser }) {
  const { lang } = useLang();
  const [mine, setMine] = useState([]);
  const [country, setCountry] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [presentRd, setPresentRd] = useState(null);
  const [showCountry, setShowCountry] = useState(false);

  const load = () => {
    api("/b2c/rewards", { b2c: true }).then((r) => { setMine(r.mine || []); setCountry(r.country || []); }).catch(() => { setMine([]); setCountry([]); });
    api("/b2c/redemptions", { b2c: true }).then((r) => setRedemptions(r.redemptions || [])).catch(() => setRedemptions([]));
  };
  useEffect(() => { load(); }, []);

  async function redeem(r) {
    setBusyId(r.id);
    try {
      const res = await api(`/b2c/rewards/${r.id}/redeem`, { method: "POST", b2c: true });
      toast.success(t("mc.rewardRedeemed") + " " + res.redemption.code);
      load();
      await refreshUser();
    } catch (e) { toast.error(e.message); } finally { setBusyId(null); }
  }

  // Group country offers by city (already sorted by city server-side).
  const countryByCity = country.reduce((acc, r) => {
    const city = (r.partner_city || "").trim() || t("mc.offersInCountry");
    (acc[city] = acc[city] || []).push(r);
    return acc;
  }, {});

  // Active vouchers only: used ones are archived and live in the points history.
  const activeRedemptions = redemptions.filter((rd) => rd.status !== "used");

  return (
    <Section title={t("mc.rewardsTitle")} testid="b2c-rewards">
      <p className="text-sm text-slate-500 -mt-3 mb-4">{t("mc.rewardsDesc")} <b className="text-findo-navy">{user.points_balance || 0} {t("mc.scorePts")}</b>.</p>

      {/* Zone 1 — Récompenses pour vous (deposit partners) */}
      <div data-testid="b2c-rewards-mine">
        <p className="text-sm font-bold text-slate-700 mb-1 flex items-center gap-1.5"><Gift size={15} className="text-findo-indigo" /> {t("mc.rewardsForYou")}</p>
        <p className="text-xs text-slate-400 mb-3">{t("mc.rewardsForYouDesc")}</p>
        {mine.length === 0 ? (
          <Empty icon={Gift} text={t("mc.rewardsMineNone")} />
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="b2c-rewards-mine-list">
            {mine.map((r) => <RewardCard key={r.id} r={r} user={user} t={t} busyId={busyId} onRedeem={redeem} />)}
          </div>
        )}
      </div>

      {/* Zone 2 — Offres dans mon pays (hidden by default) */}
      {country.length > 0 && (
        <div className="mt-5" data-testid="b2c-rewards-country">
          <button
            type="button"
            onClick={() => setShowCountry((v) => !v)}
            className="w-full flex items-center justify-between gap-2 rounded-xl border border-border bg-findo-cream/60 hover:bg-findo-cream px-4 py-3 transition-colors"
            data-testid="toggle-country-offers"
          >
            <span className="flex items-center gap-2 text-sm font-semibold text-findo-navy"><MapPin size={15} className="text-findo-indigo" /> {showCountry ? t("mc.hideCountryOffers") : t("mc.showCountryOffers")} <span className="text-xs font-normal text-slate-400">({country.length})</span></span>
            <ChevronDown size={18} className={`text-slate-400 transition-transform ${showCountry ? "rotate-180" : ""}`} />
          </button>
          {showCountry && (
            <div className="mt-3 space-y-4" data-testid="b2c-rewards-country-list">
              <p className="text-xs text-slate-400">{t("mc.offersInCountryDesc")}</p>
              {Object.entries(countryByCity).map(([city, list]) => (
                <div key={city}>
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1"><MapPin size={12} /> {city}</p>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {list.map((r) => <RewardCard key={r.id} r={r} user={user} t={t} busyId={busyId} onRedeem={redeem} />)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeRedemptions.length > 0 && (
        <div className="mt-5">
          <p className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-1.5"><Ticket size={15} /> {t("mc.myRewards")}</p>
          <div className="space-y-2" data-testid="b2c-redemptions-list">
            {activeRedemptions.map((rd) => {
              const ready = !rd.expired;
              return (
              <div key={rd.id} className="bg-white rounded-xl border border-border p-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`b2c-redemption-${rd.code}`}>
                <div className="flex items-center gap-3 min-w-0">
                  {ready && (
                    <button type="button" onClick={() => setPresentRd(rd)} className="shrink-0 rounded-lg border border-border bg-white hover:ring-2 hover:ring-findo-indigo/40 transition-shadow" title={t("mc.presentInStore")} data-testid={`voucher-qr-open-${rd.code}`}>
                      <img src={`${process.env.REACT_APP_BACKEND_URL}/api/b2c/rewards/qr/${rd.code}.png`} alt="QR" className="h-14 w-14 rounded-lg bg-white" data-testid={`voucher-qr-${rd.code}`} />
                    </button>
                  )}
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-800 text-sm truncate">{rd.reward_title} · <span className="text-slate-400 font-normal">{rd.partner_name}</span></p>
                    <p className="font-mono font-bold text-findo-navy">{rd.code}</p>
                    {rd.expires_at && <p className="text-[11px] text-slate-400">{t("mc.expiresOn")} {new Date(rd.expires_at).toLocaleDateString(lang)}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {ready && (
                    <Button size="sm" onClick={() => setPresentRd(rd)} className="rounded-full font-semibold bg-findo-indigo hover:bg-findo-indigo/90" data-testid={`voucher-present-${rd.code}`}>
                      <QrCode size={14} className="mr-1.5" /> {t("mc.presentInStore")}
                    </Button>
                  )}
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${rd.expired ? "bg-findo-danger/10 text-findo-danger" : "bg-emerald-100 text-emerald-700"}`}>
                    {rd.expired ? t("mc.redExpired") : t("mc.redReady")}
                  </span>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}
      <VoucherPresentDialog rd={presentRd} onClose={() => setPresentRd(null)} t={t} lang={lang} />
    </Section>
  );
}

function VoucherPresentDialog({ rd, onClose, t, lang }) {
  return (
    <Dialog open={!!rd} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="voucher-present-dialog" onCloseAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <QrCode size={20} className="text-findo-indigo" /> {t("mc.presentTitle")}
          </DialogTitle>
        </DialogHeader>
        {rd && (
          <div className="flex flex-col items-center text-center">
            <p className="text-sm text-slate-500 mb-3">{t("mc.presentHint")}</p>
            <div className="rounded-2xl border-2 border-findo-indigo/20 bg-white p-4 shadow-sm">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/b2c/rewards/qr/${rd.code}.png`}
                alt={`QR ${rd.code}`}
                className="h-56 w-56 mx-auto"
                data-testid="voucher-present-qr"
              />
            </div>
            <p className="mt-4 font-mono text-2xl font-extrabold tracking-wide text-findo-navy" data-testid="voucher-present-code">{rd.code}</p>
            <p className="mt-1 text-sm font-semibold text-slate-700">{rd.reward_title}</p>
            <p className="text-xs text-slate-400">{rd.partner_name}</p>
            {rd.expires_at && (
              <p className="mt-2 text-xs text-slate-400">{t("mc.expiresOn")} {new Date(rd.expires_at).toLocaleDateString(lang)}</p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PointsHistorySection({ t }) {
  const { lang } = useLang();
  const [data, setData] = useState(null);
  useEffect(() => {
    api("/b2c/points-history", { b2c: true }).then(setData).catch(() => setData({ items: [] }));
  }, []);
  const items = data?.items || [];
  if (items.length === 0) return null;
  return (
    <Section title={t("mc.historyTitle")} testid="b2c-points-history">
      <div className="bg-white rounded-2xl border border-border card-shadow divide-y divide-border" data-testid="points-history-list">
        {items.map((it, i) => (
          <div key={i} className="flex items-center justify-between gap-3 p-4" data-testid={`history-item-${i}`}>
            <div className="flex items-center gap-3 min-w-0">
              <span className={`h-9 w-9 rounded-full grid place-items-center shrink-0 ${it.delta > 0 ? "bg-emerald-100 text-emerald-600" : "bg-findo-red/10 text-findo-red"}`}>
                {it.delta > 0 ? <Trophy size={16} /> : <Gift size={16} />}
              </span>
              <div className="min-w-0">
                <p className="font-semibold text-slate-800 text-sm truncate">{it.delta > 0 ? t("mc.histEarned") : it.label}{it.partner ? ` · ${it.partner}` : ""}</p>
                <p className="text-xs text-slate-400">{it.at ? new Date(it.at).toLocaleDateString(lang) : ""}{it.code ? ` · ${it.code}` : ""}</p>
              </div>
            </div>
            <span className={`font-display font-semibold text-lg shrink-0 ${it.delta > 0 ? "text-emerald-600" : "text-findo-red"}`}>{it.delta > 0 ? `+${it.delta}` : it.delta}</span>
          </div>
        ))}
      </div>
    </Section>
  );
}

function Section({ title, testid, children }) {
  return (
    <div className="mb-10" data-testid={testid}>
      <h2 className="font-display text-2xl font-semibold text-findo-navy mb-4">{title}</h2>
      {children}
    </div>
  );
}

function Empty({ icon: Icon, text }) {
  return (
    <div className="text-center py-12 border-2 border-dashed border-border rounded-2xl">
      <Icon size={28} className="mx-auto text-slate-300" />
      <p className="mt-2 text-slate-500 text-sm">{text}</p>
    </div>
  );
}
